import { useState, useMemo, useEffect, useRef, useCallback } from "react";

import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, ReferenceLine, PieChart, Pie, Cell } from "recharts";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSiteProfile } from "./hooks/useSiteProfile";
import FleetProfileEditor, { buildFleetDemandProfile, type FleetTypeConfig } from "./FleetProfileEditor";
import { useHubProfileUpload } from "@/hooks/useHubProfileUpload";
import { UploadProfilePreview } from "./UploadProfilePreview";
import { FINANCIAL_DEFAULTS, type FinancialParams } from "./utils/financialDefaults";
import { useFinancialModel } from "./hooks/useFinancialModel";
import { deliveredEnergy, formatEurPerKwh } from "./utils/financialCalculations";
import FinancialDashboard from "./financial/FinancialDashboard";
import { generatePDF } from "./utils/pdfExport";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS (non-project-specific)
// ═══════════════════════════════════════════════════════════════════════════
const MN = ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];
const MD = [31,28,31,30,31,30,31,31,30,31,30,31];

// ═══════════════════════════════════════════════════════════════════════════
// THEME COLORS (kept for Recharts & dynamic inline colors)
// ═══════════════════════════════════════════════════════════════════════════
const C = {
  pri: '#0B1F4A', sec: '#00A3C4', acc: '#FFC233',
  bg1: '#FFFFFF', bg2: '#F5F8FB', bg3: '#EEF2F7',
  t1: '#0B1F4A', t2: '#3D4F6F', t3: '#8899AA',
  bdr: '#D9E2EC',
  grn: '#10B981', org: '#F59E0B', rd: '#EF4444',
  ch: ['#00A3C4','#0B1F4A','#FFC233','#10B981','#3D4F6F','#F59E0B','#0B1F4A','#06B6D4'],
  pg: {
    opwek: { bg: '#F5F8FB', bdr: '#D9E2EC', ico: '☀️', lbl: '#0B1F4A' },
    batt:  { bg: '#EFF6FF', bdr: '#BFDBFE', ico: '🔋', lbl: '#0B1F4A' },
    back:  { bg: '#F5F8FB', bdr: '#D9E2EC', ico: '🏭', lbl: '#3D4F6F' },
    grid:  { bg: '#F5F8FB', bdr: '#D9E2EC', ico: '⚡', lbl: '#3D4F6F' },
    vraag: { bg: '#EFF6FF', bdr: '#BFDBFE', ico: '🔌', lbl: '#0B1F4A' },
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// PASSWORD GATE — uses profile password
// ═══════════════════════════════════════════════════════════════════════════
// PasswordGate removed — authentication handled by CRM

// ═══════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function Tip({text, children}: {text: string; children: React.ReactNode}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block" onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
      {children}
      {show && (
        <div className="absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 bg-[#0B1F4A] text-white px-2.5 py-1.5 rounded-md text-[10px] leading-snug whitespace-nowrap z-[100] pointer-events-none shadow-lg">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#0B1F4A]" />
        </div>
      )}
    </div>
  );
}

function SliderInput({label,value,set,min,max,step,unit,tooltip,group}: any) {
  const g = C.pg[group] || C.pg.opwek;
  const pct = ((value-min)/(max-min))*100;
  const decimals = step < 0.1 ? 2 : step < 1 ? 1 : 0;
  const [localVal, setLocalVal] = useState(String(value));
  const [editing, setEditing] = useState(false);

  const commitValue = (raw: string) => {
    const parsed = parseFloat(raw.replace(',', '.'));
    if (isNaN(parsed)) { setLocalVal(value.toFixed(decimals)); return; }
    const clamped = Math.min(max, Math.max(min, parsed));
    set(clamped);
    setLocalVal(clamped.toFixed(decimals));
  };

  // Sync external changes when not editing
  const displayVal = editing ? localVal : value.toFixed(decimals);

  return (
    <div className="rounded-[10px] px-3.5 py-2.5" style={{background:g.bg,border:`1px solid ${g.bdr}`}}>
      <div className="flex justify-between items-center mb-1.5">
        <Tip text={tooltip || label}>
          <span className="text-[11px] font-semibold cursor-help" style={{color:g.lbl}}>
            {g.ico} {label}
          </span>
        </Tip>
        <div className="flex items-center gap-0.5">
          <input
            type="text"
            inputMode="decimal"
            value={displayVal}
            onFocus={() => { setEditing(true); setLocalVal(value.toFixed(decimals)); }}
            onChange={e => setLocalVal(e.target.value)}
            onBlur={e => { commitValue(e.target.value); setEditing(false); }}
            onKeyDown={e => { if (e.key === 'Enter') { commitValue((e.target as HTMLInputElement).value); setEditing(false); (e.target as HTMLInputElement).blur(); } }}
            className="w-16 text-right text-xs font-bold text-[#0B1F4A] bg-white px-1.5 py-0.5 rounded-md tabular-nums outline-none focus:ring-1 focus:ring-[#00A3C4]"
            style={{border:`1px solid ${g.bdr}`}}
          />
          <span className="text-[10px] text-[#8899AA]">{unit}</span>
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>set(+e.target.value)}
        className="w-full h-1.5 appearance-none rounded-sm cursor-pointer"
        style={{background:`linear-gradient(to right, ${C.sec} 0%, ${C.sec} ${pct}%, ${g.bdr} ${pct}%, ${g.bdr} 100%)`}}
      />
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-[#8899AA]">{min}{unit}</span>
        <span className="text-[9px] text-[#8899AA]">{max}{unit}</span>
      </div>
    </div>
  );
}

function KPI({icon,label,value,sub,color,status}: any) {
  const statusColor = status==='good'?C.grn:status==='warn'?C.org:status==='bad'?C.rd:C.sec;
  return (
    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#D9E2EC] shadow-sm flex flex-col gap-1"
      style={{borderTop:`3px solid ${color||statusColor}`}}>
      <div className="flex items-center gap-1.5">
        <span className="text-base">{icon}</span>
        <span className="text-[10px] font-semibold text-[#8899AA] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-xl font-extrabold text-[#0B1F4A] tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-[#8899AA] leading-snug">{sub}</div>}
    </div>
  );
}

function TabBtn({active,label,icon,onClick}: any) {
  return (
    <button onClick={onClick} className={cn(
      "px-4 py-2 text-[11px] rounded-lg cursor-pointer font-inherit whitespace-nowrap transition-all border",
      active ? "font-bold bg-[#0B1F4A] text-white border-[#0B1F4A]" : "font-medium bg-transparent text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
    )}>
      {icon} {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// INSIGHTS & TOOLTIP
// ═══════════════════════════════════════════════════════════════════════════
function genInsights(r: any) {
  const ins: any[] = [];
  if (r.ss >= 85) ins.push({ type: 'success', icon: '🎯', text: `Zelfvoorzieningsgraad is ${r.ss.toFixed(1)}% — het park is grotendeels autonoom.` });
  else if (r.ss >= 70) ins.push({ type: 'warn', icon: '⚠️', text: `Zelfvoorzieningsgraad is ${r.ss.toFixed(1)}% — overweeg meer BESS-capaciteit of PV.` });
  else ins.push({ type: 'alert', icon: '🔴', text: `Zelfvoorzieningsgraad is slechts ${r.ss.toFixed(1)}% — significant afhankelijk van grid/backup.` });
  if (r.curt > 500) ins.push({ type: 'warn', icon: '✂️', text: `${r.curt.toFixed(0)} MWh zonnestroom gaat verloren (curtailment). Grotere batterij of hogere grid-aansluiting kan dit verminderen.` });
  else if (r.curt > 0) ins.push({ type: 'info', icon: '✂️', text: `Slechts ${r.curt.toFixed(0)} MWh curtailment — goede benutting van PV.` });
  const bestMonth = r.monthly.reduce((a: any,b: any)=>a.mSS>b.mSS?a:b);
  const worstMonth = r.monthly.reduce((a: any,b: any)=>a.mSS<b.mSS?a:b);
  ins.push({ type: 'info', icon: '📅', text: `Beste maand: ${bestMonth.m} (${bestMonth.mSS.toFixed(0)}% zelfvoorzienend). Slechtste: ${worstMonth.m} (${worstMonth.mSS.toFixed(0)}%).` });
  const gridPct = r.dem > 0 ? (r.gi / r.dem * 100) : 0;
  if (gridPct > 30) ins.push({ type: 'warn', icon: '🔌', text: `${gridPct.toFixed(0)}% van de vraag komt uit het stroomnet. Grid-aansluiting is een bottleneck.` });
  if (r.bh > 500) ins.push({ type: 'warn', icon: '🏭', text: `Backup generator draait ${r.bh.toFixed(0)} vollasturen — dat is aanzienlijk. Overweeg BESS-uitbreiding.` });
  else if (r.bh > 0) ins.push({ type: 'info', icon: '🏭', text: `Backup generator draait ${r.bh.toFixed(0)} vollasturen/jaar — beperkt gebruik.` });
  const cyclesDenom = r.bE * r.dod / 100;
  const cyclesApprox = cyclesDenom > 0 ? r.monthly.reduce((s: number,m: any)=>s+m.bOut,0) / cyclesDenom : 0;
  if (cyclesApprox > 0) ins.push({ type: 'info', icon: '🔄', text: `Geschatte BESS-cycli: ~${cyclesApprox.toFixed(0)}/jaar (${(cyclesApprox/365).toFixed(1)} per dag gemiddeld).` });
  if (r.unmet > 10) ins.push({ type: 'alert', icon: '❗', text: `${r.unmet.toFixed(0)} MWh ongedekte vraag — er is onvoldoende capaciteit.` });
  if (r.degPct < 100) ins.push({ type: 'info', icon: '📉', text: `BESS capaciteit jaar ${r.projYear}: ${r.effectiveBE.toFixed(1)} MWh (${r.degPct.toFixed(1)}% van nominaal ${r.bE} MWh).` });
  return ins;
}

function CT({active,payload,label,unit,descriptions}: any) {
  if (!active||!payload?.length) return null;
  return (
    <div className="bg-white border border-[#D9E2EC] rounded-lg px-3 py-2 shadow-lg text-[11px] max-w-[280px]">
      <div className="font-bold text-[#0B1F4A] mb-1">{label}</div>
      {payload.map((p: any,i: number)=>{
        const val = typeof p.value==='number'?p.value:0;
        const desc = descriptions?.[p.dataKey];
        let context = '';
        if (desc && typeof val === 'number') {
          if (p.dataKey === 'bat') context = val >= 0 ? ' → ontladen' : ' → laden';
          else if (p.dataKey === 'grid') context = val >= 0 ? ' → import' : ' → export';
        }
        return (
          <div key={i} className="mb-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm shrink-0" style={{background:p.color}}/>
              <span className="text-[#3D4F6F]">{p.name}: <strong>{typeof p.value==='number'?p.value.toFixed(1):p.value}</strong> {unit||''}{context}</span>
            </div>
            {desc && <div className="ml-3.5 text-[9px] text-[#8899AA] italic leading-tight mt-0.5">{desc}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SANKEY FLOW DIAGRAM — PREMIUM VISUAL
// ═══════════════════════════════════════════════════════════════════════════
function SankeyFlow({ r, pv, bP, bE, effectiveBE, dod, gMax, buP, dem, blokstroomImport, blokstroomEnabled }: any) {
  const [hover, setHover] = useState<string|null>(null);

  const W = 960, H = 620;
  const maxGWh = Math.max(r.pv, r.dem, 1);
  const scale = (v: number) => Math.max(3, (v / maxGWh) * 100);

  const srcX = 80, bessX = 400, bessW = 120, dstX = 780;
  const nodeW = 180;

  const gridHasBlok = blokstroomEnabled && blokstroomImport > 0.1;
  const gridNodeH = gridHasBlok ? 80 : 52;
  const srcNodes = [
    { id: 'pv', label: '☀️ PV Productie', gwh: r.pv, sub: `${pv} MWp`, color: '#0891B2', y: 80 },
    { id: 'grid', label: '⚡ Grid Import', gwh: r.gi, sub: `${gMax} MW`, color: '#475569', y: 260 },
    { id: 'backup', label: '🏭 Backup', gwh: r.bu, sub: `${buP} MW`, color: '#D97706', y: gridHasBlok ? 420 : 400 },
  ];

  const dstNodes = [
    { id: 'demand', label: '🔌 Laadvraag', gwh: r.dem, sub: `${Math.round(dem).toLocaleString('nl-NL')} MWh/j`, color: '#1E3A5F', y: 90 },
    { id: 'curtail', label: '✂️ Curtailment', gwh: r.curt, sub: '', color: '#94A3B8', y: 320 },
    { id: 'export', label: '📤 Grid Export', gwh: r.ge, sub: '', color: '#059669', y: 410 },
    ...(r.bLoss > 0.001 ? [{ id: 'loss', label: '🔥 Verliezen', gwh: r.bLoss, sub: 'warmte', color: '#DC2626', y: 500 }] : []),
  ];

  const bessY = 180, bessH = 130;

  const rawFlows = [
    { id: 'pv-direct', from: 'pv', to: 'demand', gwh: r.direct, srcColor: '#0891B2', dstColor: '#1E3A5F', label: 'PV → Direct verbruik' },
    { id: 'pv-bess', from: 'pv', to: 'bess', gwh: Math.max(0, r.bIn - (blokstroomImport || 0)), srcColor: '#0891B2', dstColor: '#3B82F6', label: 'PV → BESS laden' },
    { id: 'pv-export', from: 'pv', to: 'export', gwh: r.ge, srcColor: '#0891B2', dstColor: '#059669', label: 'PV → Grid export' },
    { id: 'pv-curtail', from: 'pv', to: 'curtail', gwh: r.curt, srcColor: '#0891B2', dstColor: '#94A3B8', label: 'PV → Curtailment' },
    { id: 'bess-demand', from: 'bess', to: 'demand', gwh: r.bOut, srcColor: '#3B82F6', dstColor: '#1E3A5F', label: 'BESS → Laadvraag' },
    { id: 'grid-demand', from: 'grid', to: 'demand', gwh: r.gi - (blokstroomImport || 0), srcColor: '#475569', dstColor: '#1E3A5F', label: 'Grid (normaal) → Laadvraag' },
    ...(blokstroomImport > 0.1 ? [{ id: 'grid-bess-blok', from: 'grid', to: 'bess', gwh: blokstroomImport, srcColor: '#475569', dstColor: '#3B82F6', label: 'Grid (blokstroom) → BESS laden' }] : []),
    { id: 'backup-demand', from: 'backup', to: 'demand', gwh: r.bu, srcColor: '#D97706', dstColor: '#1E3A5F', label: 'Backup → Laadvraag' },
    { id: 'bess-loss', from: 'bess', to: 'loss', gwh: r.bLoss, srcColor: '#3B82F6', dstColor: '#DC2626', label: 'BESS → Verliezen (warmte)' },
  ].filter(f => f.gwh > 0.001);

  const srcOffsets: Record<string, number> = {};
  const dstOffsets: Record<string, number> = {};
  const flows = rawFlows.map(f => {
    const thickness = scale(f.gwh);
    const srcNode = srcNodes.find(n => n.id === f.from);
    const dstNode = dstNodes.find(n => n.id === f.to);
    const isBessSrc = f.from === 'bess';
    const isBessDst = f.to === 'bess';
    const isLoss = f.id === 'bess-loss';

    let srcY: number;
    if (isBessSrc && !isLoss) {
      srcY = bessY + 40 + (srcOffsets['bess'] || 0);
      srcOffsets['bess'] = (srcOffsets['bess'] || 0) + thickness + 4;
    } else if (isLoss) {
      srcY = bessY + bessH;
    } else if (srcNode) {
      srcY = srcNode.y + 10 + (srcOffsets[f.from] || 0);
      srcOffsets[f.from] = (srcOffsets[f.from] || 0) + thickness + 4;
    } else {
      srcY = 100;
    }

    let dstY: number;
    if (isBessDst) {
      dstY = bessY + 40 + (dstOffsets['bess'] || 0);
      dstOffsets['bess'] = (dstOffsets['bess'] || 0) + thickness + 4;
    } else if (f.to === 'loss') {
      dstY = 510;
    } else if (dstNode) {
      dstY = dstNode.y + 10 + (dstOffsets[f.to] || 0);
      dstOffsets[f.to] = (dstOffsets[f.to] || 0) + thickness + 4;
    } else {
      dstY = 200;
    }

    return { ...f, srcY, dstY, thickness };
  });

  const makePath = (f: typeof flows[0]) => {
    const isLoss = f.id === 'bess-loss';
    const fromIsSource = f.from !== 'bess';
    const toIsBess = f.to === 'bess';

    if (isLoss) {
      const x1 = bessX + bessW / 2;
      const y1 = f.srcY;
      const y2 = f.dstY;
      const cp1y = y1 + (y2 - y1) * 0.3;
      const cp2y = y1 + (y2 - y1) * 0.7;
      return `M ${x1},${y1} C ${x1},${cp1y} ${dstX + nodeW/2 - 10},${cp2y} ${dstX + nodeW/2 - 10},${y2}`;
    }

    const x1 = fromIsSource ? srcX + nodeW - 10 : bessX + bessW;
    const x2 = toIsBess ? bessX : dstX - 10;
    const dx = x2 - x1;
    const cpx1 = x1 + dx * 0.4;
    const cpx2 = x1 + dx * 0.6;
    return `M ${x1},${f.srcY} C ${cpx1},${f.srcY} ${cpx2},${f.dstY} ${x2},${f.dstY}`;
  };

  const pvDirect = r.direct;
  const bessOut = r.bOut;
  const gridImport = r.gi;
  const backupGen = r.bu;
  const totalSupply = pvDirect + bessOut + gridImport + backupGen;
  const selfSuff = totalSupply > 0 ? ((pvDirect + bessOut) / totalSupply * 100) : 0;

  const blokMWh = blokstroomImport || 0;
  const gridNormal = Math.max(0, gridImport - blokMWh);
  const supplyMix = [
    { label: '☀️ PV Direct', gwh: pvDirect, color: '#0891B2' },
    { label: '🔋 BESS', gwh: bessOut, color: '#3B82F6' },
    { label: '⚡ Grid (normaal)', gwh: gridNormal, color: '#475569' },
    ...(blokMWh > 0.1 ? [{ label: '⚡🌙 Grid (blokstroom)', gwh: blokMWh, color: '#64748B' }] : []),
    { label: '🏭 Backup', gwh: backupGen, color: '#D97706' },
  ];

  const renderNode = (n: typeof srcNodes[0], x: number, align: 'left'|'right') => {
    const nx = align === 'left' ? x - 10 : x - 10;
    const isGrid = n.id === 'grid';
    const h = isGrid ? gridNodeH : 52;
    return (
      <g key={n.id}>
        <rect x={nx} y={n.y - 18} width={nodeW} height={h} rx={10} fill="black" opacity={0.06} transform="translate(2,3)" />
        <rect x={nx} y={n.y - 18} width={nodeW} height={h} rx={10} fill="white" stroke="#E2E8F0" strokeWidth={1} />
        <rect x={nx} y={n.y - 8} width={5} height={h - 20} rx={2.5} fill={n.color} />
        <text x={nx + 16} y={n.y + 2} className="text-[12px] font-bold" fill="#0F172A">{n.label}</text>
        <text x={nx + 16} y={n.y + 18} className="text-[10px]" fill="#64748B">
          {n.sub ? `${n.sub} • ` : ''}{n.gwh.toFixed(0)} MWh/j
        </text>
        {isGrid && gridHasBlok && (
          <>
            <line x1={nx + 16} y1={n.y + 26} x2={nx + nodeW - 16} y2={n.y + 26} stroke="#E2E8F0" strokeWidth={0.5} />
            <text x={nx + 16} y={n.y + 40} className="text-[9px]" fill="#64748B">
              ├ Normaal (gMax): {gridNormal.toFixed(0)} MWh
            </text>
            <text x={nx + 16} y={n.y + 54} className="text-[9px]" fill="#64748B">
              └ 🌙 Blokstroom: {blokMWh.toFixed(0)} MWh
            </text>
          </>
        )}
      </g>
    );
  };

  return (
    <div>
      <div className="text-sm font-bold text-[#0B1F4A] mb-1">🔀 Energiestromen</div>
      <div className="text-[11px] text-[#8899AA] mb-4">Live Sankey-diagram — stroombreedtes evenredig met jaarlijks volume (MWh)</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: 620 }}>
        <defs>
          {flows.map(f => (
            <linearGradient key={`grad-${f.id}`} id={`grad-${f.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={f.srcColor} stopOpacity={0.7} />
              <stop offset="100%" stopColor={f.dstColor} stopOpacity={0.7} />
            </linearGradient>
          ))}
          <filter id="nodeShadow" x="-10%" y="-10%" width="130%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.08" />
          </filter>
          <filter id="flowGlow" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="bessGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#DBEAFE" />
            <stop offset="100%" stopColor="#F8FAFC" />
          </linearGradient>
        </defs>

        <line x1={srcX + nodeW/2} y1={35} x2={srcX + nodeW/2} y2={H - 20} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4 6" opacity={0.5} />
        <line x1={bessX + bessW/2} y1={35} x2={bessX + bessW/2} y2={H - 20} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4 6" opacity={0.5} />
        <line x1={dstX + nodeW/2 - 10} y1={35} x2={dstX + nodeW/2 - 10} y2={H - 20} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4 6" opacity={0.5} />

        <text x={srcX + nodeW/2 - 10} y={24} textAnchor="middle" className="text-[10px] font-bold tracking-widest" fill="#94A3B8" letterSpacing="2">BRONNEN</text>
        <line x1={srcX - 10} y1={32} x2={srcX + nodeW} y2={32} stroke="#CBD5E1" strokeWidth={0.5} />
        <text x={bessX + bessW/2} y={24} textAnchor="middle" className="text-[10px] font-bold tracking-widest" fill="#94A3B8" letterSpacing="2">OPSLAG</text>
        <line x1={bessX} y1={32} x2={bessX + bessW} y2={32} stroke="#CBD5E1" strokeWidth={0.5} />
        <text x={dstX + nodeW/2 - 10} y={24} textAnchor="middle" className="text-[10px] font-bold tracking-widest" fill="#94A3B8" letterSpacing="2">BESTEMMINGEN</text>
        <line x1={dstX - 10} y1={32} x2={dstX + nodeW} y2={32} stroke="#CBD5E1" strokeWidth={0.5} />

        {flows.map(f => {
          const d = makePath(f);
          const isHovered = hover === f.id;
          return (
            <g key={f.id} onMouseEnter={() => setHover(f.id)} onMouseLeave={() => setHover(null)} className="cursor-pointer">
              {isHovered && (
                <path d={d} fill="none" stroke={f.srcColor} strokeWidth={f.thickness + 8} strokeOpacity={0.12}
                  strokeLinecap="round" filter="url(#flowGlow)" />
              )}
              <path d={d} fill="none" stroke={`url(#grad-${f.id})`} strokeWidth={f.thickness}
                strokeOpacity={isHovered ? 0.85 : 0.5} strokeLinecap="round"
                style={{ transition: 'stroke-opacity 0.3s ease, stroke-width 0.3s ease' }} />
            </g>
          );
        })}

        <rect x={bessX - 5} y={bessY - 5} width={bessW + 10} height={bessH + 10} rx={14} fill="black" opacity={0.05} transform="translate(2,3)" />
        <rect x={bessX} y={bessY} width={bessW} height={bessH} rx={12} fill="url(#bessGrad)" stroke="#93C5FD" strokeWidth={1.5} />
        <rect x={bessX} y={bessY + 8} width={5} height={bessH - 16} rx={2.5} fill="#3B82F6" />
        <text x={bessX + bessW/2 + 4} y={bessY + 35} textAnchor="middle" className="text-[13px] font-bold" fill="#1E3A5F">🔋 BESS</text>
        <text x={bessX + bessW/2 + 4} y={bessY + 55} textAnchor="middle" className="text-[10px]" fill="#475569">{bP} MW / {bE} MWh</text>
        <text x={bessX + bessW/2 + 4} y={bessY + 72} textAnchor="middle" className="text-[9px]" fill="#94A3B8">DoD {dod}% • η {(r.bIn > 0 ? (r.bOut/r.bIn*100) : 0).toFixed(0)}%</text>

        {srcNodes.map(n => renderNode(n, srcX, 'left'))}
        {dstNodes.map(n => renderNode(n, dstX, 'right'))}

        {r.unmet > 1 && (
          <g>
            <rect x={dstX - 10} y={H - 55} width={nodeW} height={36} rx={8} fill="#FEF2F2" stroke="#FECACA" strokeWidth={1} />
            <text x={dstX + nodeW/2 - 10} y={H - 32} textAnchor="middle" className="text-[11px] font-bold" fill="#DC2626">
              ⚠️ Tekort: {r.unmet.toFixed(0)} MWh ({(r.unmet / r.dem * 100).toFixed(1)}%)
            </text>
          </g>
        )}

        {hover && (() => {
          const f = flows.find(fl => fl.id === hover);
          if (!f) return null;
          const isLoss = f.id === 'bess-loss';
          const fromIsSource = f.from !== 'bess';
          const x1 = isLoss ? bessX + bessW/2 : (fromIsSource ? srcX + nodeW - 10 : bessX + bessW);
          const x2 = isLoss ? dstX + nodeW/2 - 10 : (f.to === 'bess' ? bessX : dstX - 10);
          const mx = (x1 + x2) / 2;
          const my = (f.srcY + f.dstY) / 2 - 28;
          const pct = totalSupply > 0 ? (f.gwh / totalSupply * 100).toFixed(1) : '0.0';
          const isGridFlow = (f.id === 'grid-demand' || f.id === 'grid-bess-blok') && blokMWh > 0.1;
          const isBessFlow = f.id === 'bess-demand' && blokstroomEnabled && blokMWh > 0.1 && r.bIn > 0;
          const tooltipH = isGridFlow ? 78 : isBessFlow ? 78 : 48;

          // BESS→Laadvraag source breakdown
          let bessFromBlok = 0, bessFromPV = 0;
          if (isBessFlow) {
            const pvCharge = Math.max(0, r.bIn - blokMWh);
            bessFromPV = r.bIn > 0 ? r.bOut * (pvCharge / r.bIn) : 0;
            bessFromBlok = r.bIn > 0 ? r.bOut * (blokMWh / r.bIn) : 0;
          }

          return (
            <g>
              <rect x={mx - 120} y={my - 10} width={240} height={tooltipH} rx={8} fill="#0F172A" opacity={0.95} />
              <polygon points={`${mx - 6},${my + tooltipH - 10} ${mx + 6},${my + tooltipH - 10} ${mx},${my + tooltipH - 2}`} fill="#0F172A" opacity={0.95} />
              <text x={mx} y={my + 8} textAnchor="middle" className="text-[10px] font-semibold" fill="white">{f.label}</text>
              <text x={mx} y={my + 24} textAnchor="middle" className="text-[10px]" fill="#67E8F9">{f.gwh.toFixed(0)} MWh/jaar • {pct}%</text>
              {isGridFlow && (
                <>
                  <text x={mx} y={my + 40} textAnchor="middle" className="text-[9px]" fill="#CBD5E1">Grid Import totaal: {r.gi.toFixed(0)} MWh</text>
                  <text x={mx} y={my + 52} textAnchor="middle" className="text-[9px]" fill="#94A3B8">├ Normaal → Laadvraag: {gridNormal.toFixed(0)} MWh</text>
                  <text x={mx} y={my + 64} textAnchor="middle" className="text-[9px]" fill="#A5B4FC">└ 🌙 Blokstroom → BESS: {blokMWh.toFixed(0)} MWh</text>
                </>
              )}
              {isBessFlow && (
                <>
                  <text x={mx} y={my + 40} textAnchor="middle" className="text-[9px]" fill="#CBD5E1">Oorsprong BESS-energie:</text>
                  <text x={mx} y={my + 52} textAnchor="middle" className="text-[9px]" fill="#67E8F9">├ ☀️ Via PV laden: {bessFromPV.toFixed(0)} MWh ({(r.bOut > 0 ? bessFromPV/r.bOut*100 : 0).toFixed(0)}%)</text>
                  <text x={mx} y={my + 64} textAnchor="middle" className="text-[9px]" fill="#A5B4FC">└ 🌙 Via blokstroom: {bessFromBlok.toFixed(0)} MWh ({(r.bOut > 0 ? bessFromBlok/r.bOut*100 : 0).toFixed(0)}%)</text>
                </>
              )}
            </g>
          );
        })()}
      </svg>

      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <div className="text-[11px] font-bold text-muted-foreground mb-3 tracking-wide">LEVERINGSMIX — AANDEEL PER BRON</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {supplyMix.map(s => {
            const pct = totalSupply > 0 ? (s.gwh / totalSupply * 100) : 0;
            return (
              <div key={s.label} className="rounded-lg border border-border bg-background p-3">
                <div className="text-[11px] font-semibold text-foreground mb-1">{s.label}</div>
                <div className="text-xl font-bold" style={{ color: s.color }}>{pct.toFixed(1)}%</div>
                <div className="w-full h-2 rounded-full bg-muted mt-1.5 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: s.color }} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">{s.gwh.toFixed(0)} MWh</div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between mt-3 pt-3 border-t border-border text-[11px]">
          <span className="text-muted-foreground">Totale levering: <strong className="text-foreground">{totalSupply.toFixed(0)} MWh</strong></span>
          <span className="text-muted-foreground">Zelfvoorzieningsgraad: <strong className={selfSuff >= 85 ? 'text-green-600' : selfSuff >= 70 ? 'text-amber-600' : 'text-red-600'}>{selfSuff.toFixed(1)}%</strong></span>
          {r.curt > 1 && <span className="text-muted-foreground">Curtailment: <strong className="text-foreground">{r.curt.toFixed(0)} MWh</strong></span>}
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground mt-2 italic">ℹ️ Hover over een stroom voor details. Stroombreedtes zijn evenredig met het jaarlijks energievolume.</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════
function EnergyParkDashboard({ initialParams, onParamsChange }: { initialParams?: Record<string, any>; onParamsChange?: (params: Record<string, any>) => void }) {
  const { profile, profiles, setProfileById } = useSiteProfile();

  // Use profile defaults
  const SP = profile.solarProfile;
  const FLEET = profile.fleet;
  const CHP = profile.chargingProfile;
  const CSF = profile.seasonalFactors;
  const DOCS = profile.docs;
  const solarRef = profile.solarRefCapacity;

  // Session persistence helpers
  const simStorageKey = `sim_state_${profile.id}`;
  const loadSimState = () => {
    try {
      const raw = sessionStorage.getItem(simStorageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  };
  const savedSim = loadSimState();

  const [pv,  setPv]  = useState(initialParams?.pv ?? savedSim?.pv ?? profile.defaults.pv);
  const [bE,  setBE]  = useState(initialParams?.bE ?? savedSim?.bE ?? profile.defaults.bE);
  const [bP,  setBP]  = useState(initialParams?.bP ?? savedSim?.bP ?? profile.defaults.bP);
  const [eff, setEff] = useState(initialParams?.eff ?? savedSim?.eff ?? profile.defaults.eff);
  const [dod, setDod] = useState(initialParams?.dod ?? savedSim?.dod ?? profile.defaults.dod);
  const [buP, setBuP] = useState(initialParams?.buP ?? savedSim?.buP ?? profile.defaults.buP);
  const [gMax,setGMax]= useState(() => { const v = initialParams?.gMax ?? savedSim?.gMax ?? profile.defaults.gMax; return v > 50 ? v / 1000 : v; });
  const [dem, setDem] = useState(initialParams?.dem ?? savedSim?.dem ?? profile.defaults.dem);
  const [deg, setDeg] = useState(initialParams?.deg ?? savedSim?.deg ?? profile.defaults.deg);
  const [projYear, setProjYear] = useState(initialParams?.projYear ?? savedSim?.projYear ?? 1);

  // ─── BLOKSTROOM STATE ───
  const [blokstroomEnabled, setBlokstroomEnabled] = useState(false);
  const [blokstroomPower, setBlokstroomPower] = useState(2); // MW
  const [blokstroomStart, setBlokstroomStart] = useState(0);    // hour
  const [blokstroomEnd, setBlokstroomEnd] = useState(6);        // hour

  // (persist effect moved below mode/profile declarations)

  // Reset all sliders only when profile actually changes (not on remount)
  const prevProfileRef = useRef(profile.id);
  useEffect(() => {
    if (prevProfileRef.current !== profile.id) {
      prevProfileRef.current = profile.id;
      sessionStorage.removeItem(simStorageKey);
      setPv(profile.defaults.pv);
      setBE(profile.defaults.bE);
      setBP(profile.defaults.bP);
      setEff(profile.defaults.eff);
      setDod(profile.defaults.dod);
      setBuP(profile.defaults.buP);
      setGMax(profile.defaults.gMax);
      setDem(profile.defaults.dem);
      setDeg(profile.defaults.deg);
      setProjYear(1);
    }
  }, [profile.id]);

  const [tab,  setTab]  = useState(0);
  const [dpMo, setDpMo] = useState(5);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const [financialParams, setFinancialParams] = useState<FinancialParams>(() => ({ ...FINANCIAL_DEFAULTS }));
  const [algoMonth, setAlgoMonth] = useState(5);
  const [algoHour, setAlgoHour] = useState(12);
  const [paramTab, setParamTab] = useState<'opwek' | 'batt' | 'grid' | 'vraag'>('batt');

  // ─── MODE SELECTORS ───
  type ProfileMode = 'standaard' | 'upload';
  type DemandMode = 'standaard' | 'vloot' | 'upload';
  const [solarMode, setSolarMode] = useState<ProfileMode>(initialParams?.solarMode ?? 'standaard');
  const [gridMode, setGridMode] = useState<ProfileMode>(initialParams?.gridMode ?? 'standaard');
  const [demandMode, setDemandMode] = useState<DemandMode>(initialParams?.demandMode ?? 'standaard');
  // Per-type charging windows from Simon Loos fleet spreadsheet
  const FLEET_WINDOWS: Record<string, { start: number; end: number; pct: number }[]> = {
    "Bus met laadbak":   [{ start: 16, end: 2, pct: 90 }, { start: 2, end: 16, pct: 10 }],
    "Combi Afzet":       [{ start: 16, end: 2, pct: 100 }],
    "Combi KV-D/E LZV":  [{ start: 16, end: 2, pct: 100 }],
    "Trekker":           [{ start: 16, end: 2, pct: 90 }, { start: 2, end: 16, pct: 10 }],
    "Trekker 6x2 VLA":   [{ start: 16, end: 2, pct: 70 }, { start: 2, end: 16, pct: 30 }],
    "Trekker Bulk":      [{ start: 16, end: 2, pct: 50 }, { start: 2, end: 16, pct: 50 }],
    "Trekker DC":        [{ start: 16, end: 2, pct: 95 }, { start: 2, end: 16, pct: 5 }],
    "Trekker LWD":       [{ start: 16, end: 2, pct: 95 }, { start: 2, end: 16, pct: 5 }],
  };

  const [fleetConfig, setFleetConfig] = useState<FleetTypeConfig[]>(() =>
    FLEET.map(f => ({
      type: f.type,
      n: f.n,
      kwhPerVehicle: Math.round(f.mwh * 1e3 / f.n),
      windows: FLEET_WINDOWS[f.type] ?? [
        { start: 16, end: 2, pct: 72 },
        { start: 2, end: 16, pct: 28 },
      ],
    }))
  );

  const [fleetHourly, fleetTotalKwh] = useMemo(() => buildFleetDemandProfile(fleetConfig), [fleetConfig]);
  const fleetDemMwh = fleetTotalKwh / 1e3;

  const fleetCHP = useMemo(() => {
    const total = fleetHourly.reduce((a, b) => a + b, 0);
    if (total <= 0) return CHP;
    const avg = total / 24;
    return fleetHourly.map(h => h / avg);
  }, [fleetHourly, CHP]);

  // ─── CUSTOM PROFILE UPLOADS ───
  const { profiles: uploadedProfiles, errors: uploadErrors, loading: uploadLoading, handleSolarUpload, handleConsumptionUpload, handleGridUpload, clearSolar, clearConsumption, clearGrid, restoreProfiles } = useHubProfileUpload();

  // Restore saved profiles from initialParams on mount
  const profilesRestoredRef = useRef(false);
  useEffect(() => {
    if (!profilesRestoredRef.current && initialParams?.uploadedProfiles) {
      profilesRestoredRef.current = true;
      restoreProfiles(initialParams.uploadedProfiles);
    }
  }, [initialParams, restoreProfiles]);

  // Persist sim state to sessionStorage on every change
  useEffect(() => {
    const params: Record<string, any> = { pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear, blokstroomEnabled, blokstroomPower, blokstroomStart, blokstroomEnd, solarMode, gridMode, demandMode };
    if (uploadedProfiles.consumptionProfile || uploadedProfiles.solarProfile || uploadedProfiles.gridProfile) {
      params.uploadedProfiles = {
        consumptionProfile: uploadedProfiles.consumptionProfile,
        consumptionDemandMwh: uploadedProfiles.consumptionDemandMwh,
        consumptionStats: uploadedProfiles.consumptionStats,
        solarProfile: uploadedProfiles.solarProfile,
        solarStats: uploadedProfiles.solarStats,
        gridProfile: uploadedProfiles.gridProfile,
        gridPeakKw: uploadedProfiles.gridPeakKw,
        gridDemandMwh: uploadedProfiles.gridDemandMwh,
        gridStats: uploadedProfiles.gridStats,
      };
    }
    sessionStorage.setItem(simStorageKey, JSON.stringify(params));
    onParamsChange?.(params);
  }, [pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear, blokstroomEnabled, blokstroomPower, blokstroomStart, blokstroomEnd, solarMode, gridMode, demandMode, uploadedProfiles, simStorageKey]);

  // ─── DE GEUPLOADE NETPIEK WORDT DE NETAANSLUITING ───
  //
  // Zie de uitgebreide toelichting in hub-v2/hooks/useHubV2State.ts; dezelfde
  // fout stond hier. Kort: de knop belooft "Verbruiksprofiel importeren
  // (Piekvermogen)", het bestand werd gelezen en getoond, en geen enkele plek
  // in de simulatie gebruikte het. In upload-modus verdween bovendien de schuif
  // "Grid Max" terwijl de badge ernaast de oude handmatige waarde bleef tonen.
  const gridPeakKw = uploadedProfiles.gridPeakKw;
  useEffect(() => {
    if (gridMode !== 'upload' || !gridPeakKw || gridPeakKw <= 0) return;
    const afgeleidMw = Math.round((gridPeakKw / 1000) * 100) / 100;
    setGMax(prev => (prev === afgeleidMw ? prev : afgeleidMw));
  }, [gridMode, gridPeakKw]);

  // Resolve active profiles based on modes
  const activeSP = useMemo(() => {
    if (solarMode === 'upload' && uploadedProfiles.solarProfile) return uploadedProfiles.solarProfile;
    return SP;
  }, [solarMode, uploadedProfiles.solarProfile]);

  const activeCHP = useMemo(() => {
    if (demandMode === 'upload' && uploadedProfiles.consumptionProfile) return uploadedProfiles.consumptionProfile;
    if (demandMode === 'vloot') return fleetCHP;
    return CHP;
  }, [demandMode, uploadedProfiles.consumptionProfile, fleetCHP, CHP]);

  const resolvedDem = useMemo(() => {
    if (demandMode === 'upload' && uploadedProfiles.consumptionDemandMwh) return uploadedProfiles.consumptionDemandMwh;
    if (demandMode === 'vloot') return fleetDemMwh;
    return dem;
  }, [demandMode, uploadedProfiles.consumptionDemandMwh, fleetDemMwh, dem]);

  const effectiveBE = useMemo(() => bE * Math.pow(1 - deg/100, projYear - 1), [bE, deg, projYear]);
  const degPct = bE > 0 ? (effectiveBE / bE) * 100 : 100;

  const scenarios = profile.scenarios.map(s => ({
    ...s,
  }));

  // ─── UNDO/REDO for simulation params ───
  type SimParamsSnapshot = { pv: number; bE: number; bP: number; eff: number; dod: number; buP: number; gMax: number; dem: number; deg: number; projYear: number };
  const getSnapshot = (): SimParamsSnapshot => ({ pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear });
  const applySnapshot = (s: SimParamsSnapshot) => { setPv(s.pv); setBE(s.bE); setBP(s.bP); setEff(s.eff); setDod(s.dod); setBuP(s.buP); setGMax(s.gMax); setDem(s.dem); setDeg(s.deg); setProjYear(s.projYear); };

  const [simPast, setSimPast] = useState<SimParamsSnapshot[]>([]);
  const [simFuture, setSimFuture] = useState<SimParamsSnapshot[]>([]);
  const lastSnapshotRef = useRef<string>(JSON.stringify(getSnapshot()));

  // Record history on param changes (debounced via effect)
  useEffect(() => {
    const current = JSON.stringify(getSnapshot());
    if (current !== lastSnapshotRef.current) {
      const prev: SimParamsSnapshot = JSON.parse(lastSnapshotRef.current);
      setSimPast(p => [...p.slice(-49), prev]);
      setSimFuture([]);
      lastSnapshotRef.current = current;
    }
  }, [pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  const simUndo = useCallback(() => {
    if (simPast.length === 0) return;
    const snap = getSnapshot();
    setSimFuture(f => [snap, ...f.slice(0, 49)]);
    const p = [...simPast];
    const last = p.pop()!;
    setSimPast(p);
    applySnapshot(last);
    lastSnapshotRef.current = JSON.stringify(last);
  }, [simPast, pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  const simRedo = useCallback(() => {
    if (simFuture.length === 0) return;
    const snap = getSnapshot();
    setSimPast(p => [...p.slice(-49), snap]);
    const f = [...simFuture];
    const next = f.shift()!;
    setSimFuture(f);
    applySnapshot(next);
    lastSnapshotRef.current = JSON.stringify(next);
  }, [simFuture, pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) simRedo();
        else simUndo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        simRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [simUndo, simRedo]);

  function applySc(v: any) { setPv(v.pv); setBE(v.bE); setBP(v.bP); setEff(v.eff); setDod(v.dod); setBuP(v.buP); setGMax(v.gMax); setDem(v.dem); setDeg(v.deg); setProjYear(v.projYear); setDemandMode('standaard'); setSolarMode('standaard'); setGridMode('standaard'); }

  // ─── SIMULATION ENGINE ───
  const r = useMemo(() => {
    const sqEff = Math.sqrt(eff/100);
    const usable = effectiveBE * (dod/100);
    const scale = pv / solarRef;
    let soc = usable * 0.5;
    let tPV=0, tDem=0, tDirect=0, tBIn=0, tBOut=0, tGI=0, tGE=0, tBU=0, tCurt=0, tUnmet=0;
    let tBlokstroomImport = 0;
    const monthly: any[] = [];
    const daily = Array.from({length:12},()=>Array.from({length:24},()=>({pv:0,dem:0,bat:0,grid:0,gridBlok:0,bu:0,soc:0,n:0,bChargePV:0,bChargeBlok:0,bDischarge:0})));

    // Blokstroom helper: check if hour is in window (handles midnight crossover)
    const isBlokstroomHour = (h: number) => {
      if (!blokstroomEnabled) return false;
      if (blokstroomStart < blokstroomEnd) return h >= blokstroomStart && h < blokstroomEnd;
      return h >= blokstroomStart || h < blokstroomEnd; // e.g., 22:00-06:00
    };

    for (let m=0;m<12;m++){
      let mPV=0,mDem=0,mDirect=0,mBI=0,mBO=0,mGI=0,mGE=0,mBU=0,mCurt=0,mUnmet=0;
      for(let d=0;d<MD[m];d++){
        for(let h=0;h<24;h++){
          const pvH = activeSP[m][h] * scale / 1000;
          const demH = (resolvedDem * activeCHP[h] * CSF[m]) / (24 * 365.25 * (activeCHP.reduce((a,b)=>a+b,0)/24) * (CSF.reduce((a,b)=>a+b,0)/12));
          const net = pvH - demH;

          // Effective grid max: base + blokstroom extra during blokstroom hours
          const effectiveGMax = isBlokstroomHour(h) ? gMax + blokstroomPower : gMax;

          let bIn=0,bOut=0,gi=0,ge=0,bu=0,curt=0,unmet=0,direct=0;

          if(net>=0){
            direct=demH;
            let surplus=net;
            const canCharge=Math.min(surplus, bP, (usable-soc)/sqEff);
            if(canCharge>0){bIn=canCharge;surplus-=canCharge;soc=Math.min(usable,soc+canCharge*sqEff);}
            const canExport=Math.min(surplus,gMax);
            if(canExport>0){ge=canExport;surplus-=canExport;}
            if(surplus>0)curt=surplus;
          } else {
            direct=pvH;
            let deficit=-net;
            const rawDischarge=Math.min(deficit/sqEff,bP,soc);
            const canDischarge=rawDischarge*sqEff;
            if(canDischarge>0){bOut=canDischarge;deficit-=canDischarge;soc=Math.max(0,soc-rawDischarge);}
            const canImport=Math.min(deficit,effectiveGMax);
            if(canImport>0){
              gi=canImport;deficit-=canImport;
              // Track blokstroom portion
              if(isBlokstroomHour(h)){
                const baseImport = Math.min(canImport, gMax);
                const blokExtra = canImport - baseImport;
                tBlokstroomImport += blokExtra;
              }
            }
            const canBU=Math.min(deficit,buP);
            if(canBU>0){bu=canBU;deficit-=canBU;}
            if(deficit>0.0001)unmet=deficit;
          }
          // Proactief blokstroom laden: batterij opladen tijdens blokstroom-uren (los van gMax)
          if(isBlokstroomHour(h) && soc < usable){
            const chargeRoom=(usable-soc)/sqEff;
            const chargePowerLeft=Math.max(0,bP-bIn);
            const blokCharge=Math.min(blokstroomPower,chargePowerLeft,chargeRoom);
            if(blokCharge>0){
              bIn+=blokCharge;gi+=blokCharge;soc+=blokCharge*sqEff;
              tBlokstroomImport+=blokCharge;
            }
          }

          soc=Math.max(0,Math.min(usable,soc));
          tPV+=pvH;tDem+=demH;tDirect+=direct;tBIn+=bIn;tBOut+=bOut;tGI+=gi;tGE+=ge;tBU+=bu;tCurt+=curt;tUnmet+=unmet;
          mPV+=pvH;mDem+=demH;mDirect+=direct;mBI+=bIn;mBO+=bOut;mGI+=gi;mGE+=ge;mBU+=bu;mCurt+=curt;mUnmet+=unmet;

          // Track battery charge sources for dagprofiel
          let bChargePV = 0, bChargeBlok = 0;
          if(net >= 0) {
            // During surplus: battery charges from PV
            bChargePV = bIn;
          }
          // After proactive blokstroom charging above (line 706-714), bIn may have increased
          // The blokstroom portion is the blokCharge added in the proactive block
          if(isBlokstroomHour(h) && soc < usable) {
            // Already handled above, but we need to track the blokstroom portion
            // bIn was increased by blokCharge, gi was increased by blokCharge
            // We calculate it as: total bIn minus what was from PV
            bChargeBlok = Math.max(0, bIn - bChargePV);
          }

          const dd=daily[m][h];dd.pv+=pvH;dd.dem+=demH;dd.bat+=(bOut-bIn);dd.grid+=(gi-ge);dd.gridBlok+=(isBlokstroomHour(h) ? gi : 0);dd.bu+=bu;dd.soc+=soc;dd.n++;dd.bChargePV+=bChargePV;dd.bChargeBlok+=bChargeBlok;dd.bDischarge+=bOut;
        }
      }
      const mLocal = mDirect + mBO;
      const mSS = mDem > 0 ? (mLocal / mDem) * 100 : 0;
      monthly.push({m:MN[m],pv:mPV,dem:mDem,direct:mDirect,bIn:mBI,bOut:mBO,gi:mGI,ge:mGE,bu:mBU,curt:mCurt,unmet:mUnmet,mSS});
    }

    const local = tDirect+tBOut;
    const ss = tDem>0?(local/tDem)*100:0;
    const bLoss = tBIn-tBOut;

    return {
      pv:tPV,dem:tDem,direct:tDirect,bIn:tBIn,bOut:tBOut,
      gi:tGI,ge:tGE,bu:tBU,curt:tCurt,unmet:tUnmet,
      bLoss:bLoss,ss,bh:buP>0?tBU/buP:0,monthly,daily,bE,dod,
      pvUtil:tPV>0?((tDirect+tBIn)/tPV*100):0,
      effectiveBE, degPct, projYear,
      blokstroomImport: tBlokstroomImport,
    };
  }, [pv,effectiveBE,bP,eff,dod,buP,gMax,resolvedDem,activeCHP,activeSP,CSF,solarRef,blokstroomEnabled,blokstroomPower,blokstroomStart,blokstroomEnd]);

  const insights = useMemo(()=>genInsights(r),[r]);

  // ─── FINANCIAL MODEL ───
  const numTrucks = demandMode === 'vloot'
    ? fleetConfig.reduce((s, f) => s + f.n, 0)
    : FLEET.reduce((s, f) => s + f.n, 0);
  // `totalDelivered` is de VRAAG (`r.dem`); `unmet` is het deel daarvan dat net,
  // batterij en aggregaat niet konden dekken (opgeteld in de lus hierboven,
  // `tUnmet`). Dat tekort ging niet mee naar het financiële model, waardoor de
  // businesscase de volledige vraag factureerde: op SL 18-3 (Wognum) 14.840 van
  // de 18.994 MWh (78,1 %) tegen €0,30 laadtarief + €0,12 ERE → NPV €75.304.375
  // op €5.483.500 investering, terugverdientijd 0,76 jaar. Met `unmet` erbij
  // rekent `deliveredEnergy()` de omzet over de 4.154 MWh die overblijft.
  const financialSim = useMemo(() => ({
    pvProduction: r.pv, totalDelivered: r.dem, gridImport: r.gi,
    gridExport: r.ge, backupUsed: r.bu, curtailment: r.curt, bLoss: r.bLoss,
    blokstroomImport: r.blokstroomImport ?? 0,
    unmet: r.unmet,
  }), [r]);
  const financialSys = useMemo(() => ({
    pvCapacity: pv, bessEnergy: bE, bessPower: bP, backupPower: buP, gridMax: gMax, numTrucks,
  }), [pv, bE, bP, buP, gMax, numTrucks]);
  const financialModel = useFinancialModel(financialSim, financialSys, financialParams, deg);
  const handleFinancialParamChange = (key: keyof FinancialParams, value: number) => {
    setFinancialParams(prev => ({ ...prev, [key]: value }));
  };

  // ─── HOUR DISPATCH CALCULATOR ───
  const calcHourDispatch = (month: number, hour: number) => {
    const sqEff = Math.sqrt(eff/100);
    const usable = effectiveBE * (dod/100);
    const scale = pv / solarRef;
    const pvH = activeSP[month][hour] * scale / 1000;
    const demH = (resolvedDem * activeCHP[hour] * CSF[month]) / (24 * 365.25 * (activeCHP.reduce((a,b)=>a+b,0)/24) * (CSF.reduce((a,b)=>a+b,0)/12));
    const net = pvH - demH;

    let soc = usable * 0.5;
    for (let m=0; m<12; m++) {
      for (let d=0; d<MD[m]; d++) {
        for (let h=0; h<24; h++) {
          if (m===month && d===0 && h===hour) break;
          const pv_ = activeSP[m][h] * scale / 1000;
          const dem_ = (resolvedDem*activeCHP[h]*CSF[m]) / (24*365.25*(activeCHP.reduce((a,b)=>a+b,0)/24)*(CSF.reduce((a,b)=>a+b,0)/12));
          const n_ = pv_ - dem_;
          if (n_>=0) {
            const cc=Math.min(n_, bP, (usable-soc)/sqEff);
            if(cc>0) soc=Math.min(usable,soc+cc*sqEff);
          } else {
            const cd=Math.min(-n_,bP,soc*sqEff);
            if(cd>0) soc=Math.max(0,soc-cd/sqEff);
          }
          soc=Math.max(0,Math.min(usable,soc));
        }
        if (m===month && d===0) break;
      }
      if (m===month) break;
    }

    const socBefore = soc;
    let bIn=0,bOut=0,gi=0,ge=0,bu=0,curt=0,unmet=0,direct=0;
    const steps: {label:string; formula:string; value:number; color:string}[] = [];

    steps.push({label:'PV Output', formula:`SP[${MN[month]}][${hour}h] × ${scale.toFixed(2)} / 1000`, value:pvH, color:C.ch[0]});
    steps.push({label:'Laadvraag', formula:`${resolvedDem.toFixed(0)} MWh × CHP[${hour}] × CSF[${MN[month]}] / normalisatie`, value:demH, color:C.ch[5]});
    steps.push({label:'Netto (PV − Vraag)', formula:`${pvH.toFixed(3)} − ${demH.toFixed(3)} MW`, value:net, color:net>=0?C.grn:C.rd});

    if(net>=0){
      direct=demH;
      let surplus=net;
      steps.push({label:'⚡ Direct verbruik', formula:'Volledige vraag gedekt door PV', value:direct, color:C.sec});
      const canCharge=Math.min(surplus, bP, (usable-soc)/sqEff);
      if(canCharge>0){bIn=canCharge;surplus-=canCharge;soc=Math.min(usable,soc+canCharge*sqEff);}
      steps.push({label:'🔋 BESS Laden', formula:`min(surplus=${net.toFixed(3)}, bP=${bP}, (${usable.toFixed(1)}-SOC)/${sqEff.toFixed(3)})`, value:bIn, color:C.ch[1]});
      const canExport=Math.min(surplus,gMax);
      if(canExport>0){ge=canExport;surplus-=canExport;}
      steps.push({label:'📤 Grid Export', formula:`min(rest=${(surplus+ge).toFixed(3)}, gridMax=${gMax}MW)`, value:ge, color:C.ch[4]});
      if(surplus>0)curt=surplus;
      steps.push({label:'✂️ Curtailment', formula:'Resterend overschot', value:curt, color:'#94A3B8'});
    } else {
      direct=pvH;
      let deficit=-net;
      steps.push({label:'⚡ Direct verbruik', formula:'Alle PV direct benut', value:direct, color:C.sec});
      const rawDischarge=Math.min(deficit/sqEff,bP,soc);
      const canDischarge=rawDischarge*sqEff;
      if(canDischarge>0){bOut=canDischarge;deficit-=canDischarge;soc=Math.max(0,soc-rawDischarge);}
      steps.push({label:'🔋 BESS Ontladen', formula:`min(tekort/${sqEff.toFixed(3)}=${(deficit+canDischarge).toFixed(3)}/${sqEff.toFixed(3)}, bP=${bP}, SOC=${(soc+rawDischarge).toFixed(3)})`, value:bOut, color:C.ch[1]});
      const isBlokHour = blokstroomEnabled && (blokstroomStart < blokstroomEnd ? (hour >= blokstroomStart && hour < blokstroomEnd) : (hour >= blokstroomStart || hour < blokstroomEnd));
      const algoEffectiveGMax = isBlokHour ? gMax + blokstroomPower : gMax;
      const canImport=Math.min(deficit,algoEffectiveGMax);
      if(canImport>0){gi=canImport;deficit-=canImport;}
      steps.push({label:'📥 Grid Import', formula:`min(rest=${(deficit+gi).toFixed(3)}, gridMax=${algoEffectiveGMax.toFixed(2)}MW${isBlokHour ? ' [blokstroom]' : ''})`, value:gi, color:C.ch[4]});
      const canBU=Math.min(deficit,buP);
      if(canBU>0){bu=canBU;deficit-=canBU;}
      steps.push({label:'🏭 Backup', formula:`min(rest=${(deficit+bu).toFixed(3)}, buP=${buP}MW)`, value:bu, color:C.org});
      if(deficit>0.0001)unmet=deficit;
      steps.push({label:'❗ Ongedekt', formula:'Resterend tekort', value:unmet, color:C.rd});
    }
    soc=Math.max(0,Math.min(usable,soc));

    return {pvH,demH,net,direct,bIn,bOut,gi,ge,bu,curt,unmet,socBefore,socAfter:soc,steps,isOverschot:net>=0};
  };

  const algoDispatch = useMemo(()=>calcHourDispatch(algoMonth, algoHour),[algoMonth,algoHour,pv,effectiveBE,bP,eff,dod,buP,gMax,resolvedDem,activeCHP,activeSP,CSF,solarRef]);

  const validation = useMemo(()=>{
    const supply = r.pv + r.gi + r.bu;
    const demand = r.dem + r.bLoss + r.ge + r.curt;
    const balance = supply - demand;
    const deviation = supply > 0 ? Math.abs(balance) / supply * 100 : 0;
    return {supply,demand,balance,deviation};
  },[r]);

  const dpData = useMemo(()=>{
    return Array.from({length:24},(_,h)=>{
      const d=r.daily[dpMo][h];
      const n=d.n||1;
      const gridTotal = d.grid/n;
      const gridBlok = blokstroomEnabled ? d.gridBlok/n : 0;
      const gridNormaal = gridTotal > 0 ? Math.max(0, gridTotal - gridBlok) : gridTotal;
      const batDischarge = d.bDischarge/n;
      const batChargePV = -(d.bChargePV/n);
      const batChargeBlok = -(d.bChargeBlok/n);
      return {h:`${h}:00`,pv:d.pv/n,dem:d.dem/n,bat:d.bat/n,grid:gridNormaal,gridBlok,bu:d.bu/n,soc:d.soc/n/(effectiveBE*(dod/100))*100,batDischarge,batChargePV,batChargeBlok};
    });
  },[r,dpMo,effectiveBE,dod,blokstroomEnabled]);

  const supplyMix = useMemo(()=>{
    const data: any[] = [];
    if (r.direct>0) data.push({name:'PV Direct',value:+r.direct.toFixed(2),color:C.ch[0]});
    if (r.bOut>0) data.push({name:'BESS',value:+r.bOut.toFixed(2),color:C.ch[1]});
    if (r.gi>0) data.push({name:'Grid Import',value:+r.gi.toFixed(2),color:C.ch[4]});
    if (r.bu>0) data.push({name:'Backup',value:+r.bu.toFixed(2),color:C.ch[5]});
    return data;
  },[r]);

  const ssStatus = r.ss>=85?'good':r.ss>=70?'warn':'bad';

  // Ongedekte vraag: wat de simulatie niet kon leveren en de businesscase dus
  // ook niet mag factureren. Stond tot nu toe alleen in de energiebalans als
  // "informatief" en in de dichtgeklapte inzichtenlijst; op SL 18-3 (Wognum)
  // ging het om 14.840 van 18.994 MWh (78,1 %) zonder dat de bovenste
  // resultatenstrook er iets van liet zien.
  // Eén formule, één plek: dit is exact het getal dat `generateCashflows` als
  // omzetbasis gebruikt, want het komt uit dezelfde `deliveredEnergy(financialSim)`.
  // Een eigen `Math.max(0, r.dem - r.unmet)` hier zou banner en businesscase uit
  // elkaar laten lopen zodra één van beide verandert.
  const deliveredMwh = deliveredEnergy(financialSim);
  const unmetPct = r.dem > 0 ? (r.unmet / r.dem) * 100 : 0;
  // 1 % is de drempel voor de rode banner: daaronder is het afrondingsruis in
  // de uurdispatch, daarboven verschuift het de businesscase merkbaar.
  const unmetIsMaterial = unmetPct >= 1;

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleExportPDF = async () => {
    setPrintMode(true);
    setPdfLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    try {
      const footerText = `${profile.company} — Simulatierapport ${profile.version}`;
      await generatePDF('print-report', `${profile.id}-rapport.pdf`, false, footerText);
    } catch (e) {
      console.error('PDF generation failed:', e);
    } finally {
      setPrintMode(false);
      setPdfLoading(false);
    }
  };

  const usable = effectiveBE * (dod/100);

  const displayName = profile.name;
  const displayCompany = profile.company;
  const displayClient = profile.client;

  // ─── RENDER ───
  return (
    <div className="font-sans text-foreground">
      {pdfLoading && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl px-8 py-6 shadow-2xl text-center">
            <div className="text-lg font-bold text-[#0B1F4A] mb-2">📄 PDF wordt gegenereerd...</div>
            <div className="text-sm text-[#8899AA]">Even geduld, alle grafieken worden gerenderd.</div>
          </div>
        </div>
      )}
      <div id="print-report" className={cn("mx-auto", printMode && "bg-white")} style={printMode ? { width: '794px' } : undefined}>

        {/* COMPACT CONFIG SUMMARY */}
        <div className={cn("mb-3 px-4 py-2 bg-white rounded-lg border border-[#D9E2EC] shadow-sm flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] print:hidden", printMode && "hidden")}>
          <span className="font-bold text-[#0B1F4A]">⚙️</span>
          <span><span className="text-[#8899AA]">PV</span> <strong>{pv} MWp</strong></span>
          <span><span className="text-[#8899AA]">BESS</span> <strong>{bE} MWh / {bP} MW</strong></span>
          <span><span className="text-[#8899AA]">Backup</span> <strong>{buP} MW</strong></span>
          <span><span className="text-[#8899AA]">Grid</span> <strong>{gMax} MW</strong></span>
          <span><span className="text-[#8899AA]">Vraag</span> <strong>{resolvedDem.toFixed(0)} MWh</strong></span>
          <span><span className="text-[#8899AA]">Trucks</span> <strong>{numTrucks}</strong></span>
          <span><span className="text-[#8899AA]">Eff</span> <strong>{eff}%</strong></span>
          <span><span className="text-[#8899AA]">DoD</span> <strong>{dod}%</strong></span>
          {projYear > 1 && <span><span className="text-[#8899AA]">Jaar</span> <strong>{projYear}</strong></span>}
        </div>

        {/* TOOLBAR */}
        <div className={cn("mb-4 px-4 py-2.5 bg-card rounded-xl border border-border shadow-sm flex items-center gap-2 flex-wrap print:hidden", printMode && "hidden")} data-print-hide>
          <button onClick={simUndo} disabled={simPast.length === 0} title={`Ongedaan maken (${simPast.length})`}
            className="p-1.5 rounded-md border border-border bg-muted text-foreground hover:bg-accent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
          </button>
          <button onClick={simRedo} disabled={simFuture.length === 0} title={`Opnieuw (${simFuture.length})`}
            className="p-1.5 rounded-md border border-border bg-muted text-foreground hover:bg-accent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/></svg>
          </button>

          <div className="w-px h-5 bg-border mx-1" />

          {scenarios.map((s,i)=>(
            <Tip key={i} text={s.d}>
              <button onClick={()=>applySc(s.v)}
                className="px-2.5 py-1 text-[10px] font-semibold rounded-lg border border-border bg-muted text-foreground hover:bg-accent cursor-pointer transition-colors">
                {s.n}
              </button>
            </Tip>
          ))}

          <div className="flex-1" />

          <button onClick={handleExportPDF}
            className="px-2.5 py-1 text-[10px] font-semibold rounded-lg border border-border bg-muted text-foreground hover:bg-accent cursor-pointer transition-colors">
            📄 PDF
          </button>
        </div>

        {/* PARAMETERS — TABBED INTERFACE */}
        <div className={cn("mb-4 print:hidden", printMode && "hidden")} data-print-hide>
          <div className="bg-white rounded-xl border border-[#D9E2EC] shadow-sm overflow-hidden">
            <Tabs value={paramTab} onValueChange={(v) => setParamTab(v as typeof paramTab)}>
              <TabsList className="w-full justify-start rounded-none border-b border-[#D9E2EC] bg-[#FAFBFC] h-9 p-0.5 gap-0.5">
                <TabsTrigger value="opwek" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">☀️ Opwek</TabsTrigger>
                <TabsTrigger value="batt" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">🔋 Batterij</TabsTrigger>
                <TabsTrigger value="grid" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">⚡ Grid & Backup</TabsTrigger>
                <TabsTrigger value="vraag" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">🔌 Laadvraag & Vloot</TabsTrigger>
              </TabsList>
              <div className="p-3">
                <TabsContent value="opwek" className="mt-0">
                  {/* Mode selector — same pattern as Laadvraag */}
                  <div className="flex items-center gap-1.5 mb-3 p-2 rounded-lg bg-[#FFF7ED] border border-[#FED7AA]">
                    {([
                      ['standaard', '📊 Standaard', 'PV capaciteit handmatig instellen'],
                      ['upload', '📂 Upload', 'Zonprofiel (12×24 matrix) importeren'],
                    ] as [ProfileMode, string, string][]).map(([mode, label, tip]) => (
                      <Tip key={mode} text={tip}>
                        <button onClick={() => setSolarMode(mode)}
                          className={cn(
                            "px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                            solarMode === mode
                              ? "bg-[#0B1F4A] text-white border-[#0B1F4A]"
                              : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                          )}>
                          {label}
                        </button>
                      </Tip>
                    ))}
                    <span className="ml-auto text-xs font-bold text-[#0B1F4A] bg-white px-2 py-0.5 rounded-md border border-[#D9E2EC] tabular-nums">
                      {pv} MWp
                    </span>
                  </div>

                  {solarMode === 'standaard' && (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                      <SliderInput label="PV Capaciteit" value={pv} set={setPv} min={0} max={40} step={0.1} unit="MWp" group="opwek" tooltip="Totaal piekvermogen zonnepanelen. Zet op 0 voor scenario's zonder zon."/>
                    </div>
                  )}

                  {solarMode === 'upload' && (
                    <div className="space-y-3">
                      <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
                        📂 Upload een CSV of Excel bestand met een <strong>12×24 matrix</strong>: 12 rijen (maanden jan–dec) × 24 kolommen (uren 0–23).
                        Waarden in kW representeren het typische opwekprofiel per maand en uur. Het standaard PV-referentieprofiel wordt overschreven.
                        <br/><br/>
                        <span className="font-semibold text-foreground">Voorbeeld (CSV):</span>
                        <code className="block mt-1 bg-background rounded px-2 py-1 text-[9px] font-mono border border-border">
                          0;0;0;0;0;0.1;0.5;1.2;2.1;3.0;3.8;4.2;4.5;4.3;3.9;3.1;2.0;1.0;0.3;0;0;0;0;0<br/>
                          0;0;0;0;0;0.2;0.8;1.8;3.0;4.2;5.1;5.5;5.8;5.6;5.0;3.8;2.5;1.2;0.4;0;0;0;0;0<br/>
                          ... (12 rijen totaal)
                        </code>
                      </div>

                      {!uploadedProfiles.solarProfile ? (
                        <div>
                          <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-solar-upload"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleSolarUpload(f); e.target.value = ''; }}
                          />
                          <button onClick={() => document.getElementById('hub-solar-upload')?.click()}
                            disabled={uploadLoading.solar}
                            className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                            {uploadLoading.solar ? '⏳ Verwerken...' : '📂 Kies bestand'}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-foreground">✅ Zonprofiel geladen</span>
                            <button onClick={clearSolar}
                              className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">
                              ✕ Verwijderen
                            </button>
                          </div>
                          {uploadedProfiles.solarStats && (
                            <div className="text-[10px] text-muted-foreground space-y-0.5">
                              <div>📊 {uploadedProfiles.solarStats.dateEnd}</div>
                              <div>☀️ {uploadedProfiles.solarStats.dateStart}</div>
                            </div>
                          )}
                          {uploadedProfiles.solarProfile && (
                            <UploadProfilePreview
                              data={uploadedProfiles.solarProfile.reduce((avg, month) => {
                                month.forEach((v, h) => { avg[h] = (avg[h] || 0) + v / 12; });
                                return avg;
                              }, new Array(24).fill(0))}
                              label="Gem. zonprofiel (12-maands gem.)"
                              color="hsl(var(--chart-4))"
                            />
                          )}
                        </div>
                      )}

                      {uploadErrors.solar && (
                        <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">
                          ❌ {uploadErrors.solar}
                        </div>
                      )}

                      {/* Always show PV capacity slider in upload mode too */}
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2 mt-2">
                        <SliderInput label="PV Capaciteit (schaal)" value={pv} set={setPv} min={0} max={40} step={0.1} unit="MWp" group="opwek" tooltip="Schaalt het geüploade profiel naar dit piekvermogen."/>
                      </div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="batt" className="mt-0">
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                    <SliderInput label="BESS Energie" value={bE} set={setBE} min={0} max={100} step={0.1} unit="MWh" group="batt" tooltip="Totale opslagcapaciteit batterij (nominaal). Zet op 0 voor scenario zonder batterij."/>
                    <SliderInput label="BESS Vermogen" value={bP} set={setBP} min={0} max={50} step={0.05} unit="MW" group="batt" tooltip="Max laad/ontlaadvermogen batterij"/>
                    <SliderInput label="Efficiency" value={eff} set={setEff} min={75} max={98} step={1} unit="%" group="batt" tooltip="Round-trip efficiency batterij"/>
                    <SliderInput label="DoD" value={dod} set={setDod} min={50} max={100} step={5} unit="%" group="batt" tooltip="Depth of Discharge — bruikbaar deel"/>
                    <SliderInput label="Degradatie" value={deg} set={setDeg} min={0} max={4} step={0.5} unit="%/j" group="batt" tooltip="Jaarlijks capaciteitsverlies batterij"/>
                    <SliderInput label="Projectjaar" value={projYear} set={setProjYear} min={1} max={20} step={1} unit="" group="batt" tooltip="Jaar in de levensduur (1=nieuw)"/>
                  </div>
                </TabsContent>
                <TabsContent value="grid" className="mt-0">
                  {/* Mode selector — same pattern */}
                  <div className="flex items-center gap-1.5 mb-3 p-2 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]">
                    {([
                      ['standaard', '📊 Standaard', 'Grid & backup handmatig instellen'],
                      ['upload', '📂 Upload', 'Verbruiksprofiel importeren (Piekvermogen)'],
                    ] as [ProfileMode, string, string][]).map(([mode, label, tip]) => (
                      <Tip key={mode} text={tip}>
                        <button onClick={() => setGridMode(mode)}
                          className={cn(
                            "px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                            gridMode === mode
                              ? "bg-[#0B1F4A] text-white border-[#0B1F4A]"
                              : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                          )}>
                          {label}
                        </button>
                      </Tip>
                    ))}
                    <span className="ml-auto text-xs font-bold text-[#0B1F4A] bg-white px-2 py-0.5 rounded-md border border-[#D9E2EC] tabular-nums">
                      {gMax} MW
                    </span>
                  </div>

                  {/* Standaard: sliders + blokstroom */}
                  {gridMode === 'standaard' && (
                    <>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                        <SliderInput label="Grid Max" value={gMax} set={setGMax} min={0} max={5} step={0.01} unit="MW" group="grid" tooltip="Maximale netaansluiting"/>
                        <SliderInput label="Backup Vermogen" value={buP} set={setBuP} min={0} max={10} step={0.5} unit="MW" group="back" tooltip="Maximaal vermogen noodgenerator"/>
                      </div>
                      {/* Blokstroom */}
                      <div className="mt-3 rounded-lg border border-[#D9E2EC] bg-[#F5F8FB] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-semibold text-[#0B1F4A]">⚡ Blokstroom (nacht-transportrecht)</span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={blokstroomEnabled} onChange={() => setBlokstroomEnabled(!blokstroomEnabled)}
                              className="w-3.5 h-3.5 rounded border-[#D9E2EC] accent-[#00A3C4] cursor-pointer" />
                            <span className="text-[10px] text-[#8899AA]">{blokstroomEnabled ? 'Actief' : 'Uit'}</span>
                          </label>
                        </div>
                        {blokstroomEnabled && (
                          <>
                            <div className="mb-2 text-[10px] text-muted-foreground bg-white/60 rounded-md border border-border px-2.5 py-1.5 leading-relaxed">
                              ℹ️ Blokstroom is extra netcapaciteit die netbeheerders aanbieden tijdens daluren (meestal 's nachts). Dit staat los van je normale aansluiting (gMax). De batterij wordt proactief geladen tegen een lager tarief, zodat deze overdag kan ondersteunen bij piekbelasting.
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                              <SliderInput label="Extra Vermogen" value={blokstroomPower} set={setBlokstroomPower} min={0} max={10} step={0.1} unit="MW" group="grid" tooltip="Extra netcapaciteit tijdens blokstroom-uren bovenop de standaard aansluiting"/>
                              <SliderInput label="Start uur" value={blokstroomStart} set={setBlokstroomStart} min={0} max={23} step={1} unit="h" group="grid" tooltip="Begin van het blokstroom-venster"/>
                              <SliderInput label="Eind uur" value={blokstroomEnd} set={setBlokstroomEnd} min={0} max={23} step={1} unit="h" group="grid" tooltip="Einde van het blokstroom-venster"/>
                            </div>
                          </>
                        )}
                        {blokstroomEnabled && r.blokstroomImport > 0 && (
                          <div className="mt-2 text-[10px] text-[#3D4F6F] bg-white rounded-md border border-[#D9E2EC] px-2.5 py-1.5">
                            ⚡ Blokstroom import: <strong>{r.blokstroomImport.toFixed(0)} MWh/jaar</strong> ({(r.blokstroomImport / r.gi * 100).toFixed(1)}% van totale grid import)
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Upload mode */}
                  {gridMode === 'upload' && (
                    <div className="space-y-3">
                      <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
                        📂 Upload een CSV of Excel bestand met kwartierwaarden in <strong>Piekvermogen-formaat</strong>:
                        twee kolommen met een <strong>tijdstempel</strong> (datum + tijd) en <strong>kW-waarde</strong>.
                        Het systeem berekent automatisch het grid-verbruiksprofiel en piekvermogen.
                        <br/><br/>
                        <span className="font-semibold text-foreground">Voorbeeld:</span>
                        <code className="block mt-1 bg-background rounded px-2 py-1 text-[9px] font-mono border border-border">
                          datum;kW<br/>
                          1-1-2024 00:00:00;185.2<br/>
                          1-1-2024 00:15:00;192.8<br/>
                          1-1-2024 00:30:00;178.4<br/>
                          ...
                        </code>
                      </div>

                      {!uploadedProfiles.gridProfile ? (
                        <div>
                          <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-grid-upload"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleGridUpload(f); e.target.value = ''; }}
                          />
                          <button onClick={() => document.getElementById('hub-grid-upload')?.click()}
                            disabled={uploadLoading.grid}
                            className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                            {uploadLoading.grid ? '⏳ Verwerken...' : '📂 Kies bestand'}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-foreground">✅ Grid verbruiksprofiel geladen</span>
                            <button onClick={clearGrid}
                              className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">
                              ✕ Verwijderen
                            </button>
                          </div>
                          {uploadedProfiles.gridStats && (
                            <div className="text-[10px] text-muted-foreground space-y-0.5">
                              <div>📊 <strong>{uploadedProfiles.gridStats.rows.toLocaleString('nl-NL')}</strong> datapunten</div>
                              <div>📅 {uploadedProfiles.gridStats.dateStart} t/m {uploadedProfiles.gridStats.dateEnd}</div>
                              <div>⚡ Berekend jaarverbruik: <strong className="text-foreground">{uploadedProfiles.gridDemandMwh?.toFixed(0)} MWh</strong></div>
                              {/* Zie useHubV2State.ts: tot 8 september 2026 stond hier
                                  niet wat het bestand met de simulatie deed, omdat het
                                  er niets mee deed. */}
                              {uploadedProfiles.gridPeakKw ? (
                                <div>🔌 Piek <strong className="text-foreground">{(uploadedProfiles.gridPeakKw / 1000).toFixed(2)} MW</strong> — dit is de netaansluiting geworden</div>
                              ) : (
                                <div className="text-destructive">🔌 Geen piek uit dit bestand te halen; de netaansluiting staat nog op {gMax} MW</div>
                              )}
                            </div>
                          )}
                          {uploadedProfiles.gridProfile && (
                            <UploadProfilePreview data={uploadedProfiles.gridProfile} label="24-uurs gridprofiel (relatief)" color="hsl(var(--chart-2))" />
                          )}
                        </div>
                      )}

                      {uploadErrors.grid && (
                        <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">
                          ❌ {uploadErrors.grid}
                        </div>
                      )}

                      {/* Always show grid sliders in upload mode too */}
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2 mt-2">
                        <SliderInput label="Grid Max" value={gMax} set={setGMax} min={0} max={5} step={0.01} unit="MW" group="grid" tooltip="Maximale netaansluiting"/>
                        <SliderInput label="Backup Vermogen" value={buP} set={setBuP} min={0} max={10} step={0.5} unit="MW" group="back" tooltip="Maximaal vermogen noodgenerator"/>
                      </div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="vraag" className="mt-0">
                  {/* 3-mode selector */}
                  <div className="flex items-center gap-1.5 mb-3 p-2 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE]">
                    {([
                      ['standaard', '📊 Standaard', 'Handmatig MWh instellen'],
                      ['vloot', '🚛 Vlootprofiel', 'Per voertuigtype configureren'],
                      ['upload', '📂 Upload', 'Kwartierwaarden importeren'],
                    ] as [DemandMode, string, string][]).map(([mode, label, tip]) => (
                      <Tip key={mode} text={tip}>
                        <button onClick={() => setDemandMode(mode)}
                          className={cn(
                            "px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                            demandMode === mode
                              ? "bg-[#0B1F4A] text-white border-[#0B1F4A]"
                              : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                          )}>
                          {label}
                        </button>
                      </Tip>
                    ))}
                    <span className="ml-auto text-xs font-bold text-[#0B1F4A] bg-white px-2 py-0.5 rounded-md border border-[#D9E2EC] tabular-nums">
                      {resolvedDem.toFixed(0)} MWh/j
                    </span>
                  </div>

                  {/* MODE: Standaard */}
                  {demandMode === 'standaard' && (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                      <SliderInput label="Laadvraag" value={dem} set={setDem} min={100} max={40000} step={100} unit="MWh" group="vraag" tooltip="Jaarlijkse stroomvraag vloot"/>
                    </div>
                  )}

                  {/* MODE: Vlootprofiel */}
                  {demandMode === 'vloot' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => setFleetConfig(prev => prev.map(ft => ({ ...ft, n: 0 })))}
                          className="px-2 py-1 text-[10px] font-semibold rounded-md border border-[#D9E2EC] bg-white text-[#3D4F6F] hover:bg-[#F5F8FB] cursor-pointer transition-colors">
                          🧹 Leeg profiel
                        </button>
                        <button onClick={() => setFleetConfig(FLEET.map(f => ({
                          type: f.type, n: f.n, kwhPerVehicle: Math.round(f.mwh * 1e3 / f.n),
                          windows: [{ start: 3, end: 5, pct: 72 }, { start: 17, end: 20, pct: 28 }],
                        })))}
                          className="px-2 py-1 text-[10px] font-semibold rounded-md border border-[#D9E2EC] bg-white text-[#3D4F6F] hover:bg-[#F5F8FB] cursor-pointer transition-colors">
                          🔄 Reset naar default
                        </button>
                      </div>
                      <FleetProfileEditor config={fleetConfig} onChange={setFleetConfig} />
                    </div>
                  )}

                  {/* MODE: Upload (Piekvermogen-formaat) */}
                  {demandMode === 'upload' && (
                    <div className="space-y-3">
                      <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
                        📂 Upload een CSV of Excel bestand met kwartierwaarden in <strong>Piekvermogen-formaat</strong>:
                        twee kolommen met een <strong>tijdstempel</strong> (datum + tijd) en <strong>kW-waarde</strong>.
                        Het systeem herkent automatisch de kolommen op basis van headers zoals "tijd/datum" en "kW/vermogen/piekbelasting".
                        <br/><br/>
                        <span className="font-semibold text-foreground">Voorbeeld:</span>
                        <code className="block mt-1 bg-background rounded px-2 py-1 text-[9px] font-mono border border-border">
                          datum;kW<br/>
                          1-1-2024 00:00:00;245.3<br/>
                          1-1-2024 00:15:00;238.7<br/>
                          1-1-2024 00:30:00;251.1<br/>
                          ...
                        </code>
                      </div>

                      {!uploadedProfiles.consumptionProfile ? (
                        <div>
                          <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-consumption-upload"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleConsumptionUpload(f); e.target.value = ''; }}
                          />
                          <button onClick={() => document.getElementById('hub-consumption-upload')?.click()}
                            disabled={uploadLoading.consumption}
                            className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                            {uploadLoading.consumption ? '⏳ Verwerken...' : '📂 Kies bestand'}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-foreground">✅ Verbruiksprofiel geladen</span>
                            <button onClick={clearConsumption}
                              className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">
                              ✕ Verwijderen
                            </button>
                          </div>
                          {uploadedProfiles.consumptionStats && (
                            <div className="text-[10px] text-muted-foreground space-y-0.5">
                              <div>📊 <strong>{uploadedProfiles.consumptionStats.rows.toLocaleString('nl-NL')}</strong> datapunten</div>
                              <div>📅 {uploadedProfiles.consumptionStats.dateStart} t/m {uploadedProfiles.consumptionStats.dateEnd}</div>
                              <div>⚡ Berekende jaarlijkse vraag: <strong className="text-foreground">{uploadedProfiles.consumptionDemandMwh?.toFixed(0)} MWh</strong></div>
                              {uploadedProfiles.consumptionStats.detectedUnit && (
                                <div>🔍 Gedetecteerde eenheid: <strong className="text-foreground">{uploadedProfiles.consumptionStats.detectedUnit}</strong>{uploadedProfiles.consumptionStats.detectedUnit === 'kWh' ? ' (omgerekend naar kW)' : ''}</div>
                              )}
                            </div>
                          )}
                          {uploadedProfiles.consumptionProfile && (
                            <UploadProfilePreview data={uploadedProfiles.consumptionProfile} label="24-uurs verbruiksprofiel (relatief)" color="hsl(var(--primary))" />
                          )}
                        </div>
                      )}

                      {uploadErrors.consumption && (
                        <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">
                          ❌ {uploadErrors.consumption}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>

        {projYear > 1 && !printMode && (
          <div className="mb-4 px-4 py-2.5 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] flex items-center gap-3 print:hidden">
            <span className="text-sm">📉</span>
            <span className="text-[11px] text-[#3D4F6F]">
              <strong className="text-[#0B1F4A]">BESS capaciteit jaar {projYear}:</strong> {effectiveBE.toFixed(1)} MWh ({degPct.toFixed(1)}% van nominaal {bE} MWh) — bruikbaar: {usable.toFixed(1)} MWh
            </span>
          </div>
        )}

        {/* PRINT-ONLY: COVER PAGE */}
        {printMode && (
          <div className="print-page-cover mb-0" data-pdf-section="cover">
            <div className="flex flex-col items-center justify-center min-h-[700px] text-center">
              <div className="flex items-center gap-6 mb-8">
                {profile.logoUrl && <img src={profile.logoUrl} alt={profile.company} className="h-16 object-contain"/>}
                {profile.clientLogoUrl && <img src={profile.clientLogoUrl} alt={profile.client} className="h-12 object-contain"/>}
              </div>
              <h1 className="text-2xl font-extrabold text-[#0B1F4A] mb-2">{profile.name}</h1>
              <h2 className="text-lg font-semibold text-[#3D4F6F] mb-1">Simulatierapport</h2>
              <div className="text-sm text-[#8899AA] mb-8">
                {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })} — {profile.version}
              </div>
              <div className="bg-[#F5F8FB] border border-[#D9E2EC] rounded-xl p-6 text-left max-w-md w-full">
                <div className="text-xs font-bold text-[#0B1F4A] mb-3">📑 Inhoudsopgave</div>
                <div className="space-y-1.5 text-[11px] text-[#3D4F6F]">
                  <div className="flex justify-between"><span>1. Configuratie & KPI's</span><span className="text-[#8899AA]">2</span></div>
                  <div className="flex justify-between"><span>2. Jaaroverzicht</span><span className="text-[#8899AA]">3</span></div>
                  <div className="flex justify-between"><span>3. Dagprofiel</span><span className="text-[#8899AA]">4</span></div>
                  <div className="flex justify-between"><span>4. Zelfvoorziening</span><span className="text-[#8899AA]">5</span></div>
                  <div className="flex justify-between"><span>5. BESS Activiteit</span><span className="text-[#8899AA]">6</span></div>
                  <div className="flex justify-between"><span>6. Energiebalans</span><span className="text-[#8899AA]">7</span></div>
                  <div className="flex justify-between"><span>7. Energiestromen</span><span className="text-[#8899AA]">8</span></div>
                  <div className="flex justify-between"><span>8. Financiële Samenvatting</span><span className="text-[#8899AA]">9</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Print-only parameter summary */}
        {printMode && <div className="mb-4 bg-white rounded-xl border border-[#D9E2EC] p-4 print-section" data-pdf-section="config">
          <div className="text-xs font-bold text-[#0B1F4A] mb-2">⚙️ Configuratie</div>
          <div className="grid grid-cols-4 gap-2 text-[10px]">
            <div><span className="text-[#8899AA]">PV:</span> <strong>{pv} MWp</strong></div>
            <div><span className="text-[#8899AA]">BESS:</span> <strong>{bE} MWh / {bP} MW</strong></div>
            <div><span className="text-[#8899AA]">Efficiency:</span> <strong>{eff}%</strong></div>
            <div><span className="text-[#8899AA]">DoD:</span> <strong>{dod}%</strong></div>
            <div><span className="text-[#8899AA]">Degradatie:</span> <strong>{deg}%/j (jaar {projYear})</strong></div>
            <div><span className="text-[#8899AA]">Effectief BESS:</span> <strong>{effectiveBE.toFixed(1)} MWh</strong></div>
            <div><span className="text-[#8899AA]">Backup:</span> <strong>{buP} MW</strong></div>
            <div><span className="text-[#8899AA]">Grid Max:</span> <strong>{gMax} MW</strong></div>
            <div><span className="text-[#8899AA]">Laadvraag:</span> <strong>{resolvedDem.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} MWh</strong></div>
          </div>
        </div>}

        {/* ONGEDEKTE VRAAG — waarschuwing bovenaan de resultaten, niet weggestopt
            in de dichtgeklapte inzichtenlijst. Krijgt een eigen data-pdf-section
            zodat hij ook in het geëxporteerde rapport staat. */}
        {unmetIsMaterial && (
          <div className="mb-4 rounded-xl border-2 border-[#FECACA] bg-[#FEF2F2] px-4 py-3" data-pdf-section="unmet">
            <div className="text-[13px] font-bold text-[#991B1B]">
              ⚠️ {unmetPct.toFixed(1)}% van de laadvraag blijft ongedekt — {r.unmet.toFixed(0)} van {r.dem.toFixed(0)} MWh
            </div>
            <div className="mt-1 text-[11px] leading-relaxed text-[#7F1D1D]">
              Net ({gMax} MW), batterij ({bP} MW / {effectiveBE.toFixed(1)} MWh) en noodaggregaat ({buP} MW) halen
              de vraag niet. De businesscase rekent daarom alleen met de {deliveredMwh.toFixed(0)} MWh die
              wél geleverd wordt: ongedekte vraag levert geen laadomzet en geen ERE op.
              Verhoog de aansluiting, het batterij- of noodvermogen, of verlaag de laadvraag.
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2.5 mb-4" data-pdf-section="kpis">
          <KPI icon="☀️" label="PV Productie" value={`${r.pv.toFixed(0)} MWh`} color={C.ch[0]}/>
          <KPI icon="🔌" label="Laadvraag" value={`${r.dem.toFixed(0)} MWh`} color={C.pri}/>
          {/* Geleverd staat naast Laadvraag: die twee lopen op SL 18-3 een factor
              4,6 uiteen (4.154 vs. 18.994 MWh) en alleen de eerste is omzet. */}
          <KPI icon="📦" label="Geleverd" value={`${deliveredMwh.toFixed(0)} MWh`}
            status={unmetIsMaterial ? 'bad' : 'good'}
            sub={r.unmet > 0.0001
              ? `${r.unmet.toFixed(0)} MWh ongedekt (${unmetPct.toFixed(1)}%) — niet factureerbaar`
              : 'volledige laadvraag gedekt'}/>
          <KPI icon="✅" label="Zelfvoorziening" value={`${r.ss.toFixed(1)}%`} status={ssStatus}
            sub={r.ss>=85?'Uitstekend':r.ss>=70?'Redelijk — ruimte voor verbetering':'Aandacht nodig'}/>
          <KPI icon="⚡" label="Grid Import" value={`${r.gi.toFixed(0)} MWh`} color={C.ch[4]}/>
          <KPI icon="🏭" label="Backup" value={`${r.bu.toFixed(0)} MWh`} sub={`${r.bh.toFixed(0)} vollasturen`} color={C.org}/>
          <KPI icon="✂️" label="Curtailed" value={`${r.curt.toFixed(0)} MWh`} color={C.acc}/>
        </div>

        {/* TABS */}
        <Tabs value={printMode ? 'jaar' : ['jaar','dag','zelf','bess','balans','algo','stromen','bronnen'][tab] || 'jaar'} onValueChange={(v) => {
          const idx = ['jaar','dag','zelf','bess','balans','algo','stromen','bronnen'].indexOf(v);
          if (idx >= 0) setTab(idx);
        }}>
          <TabsList className={cn("w-full justify-start rounded-none bg-transparent h-auto p-0 gap-1 mb-3 flex-wrap print:hidden", printMode && "hidden")}>
            <TabsTrigger value="jaar" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">📊 Jaaroverzicht</TabsTrigger>
            <TabsTrigger value="dag" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">🕐 Dagprofiel</TabsTrigger>
            <TabsTrigger value="zelf" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">📈 Zelfvoorziening</TabsTrigger>
            <TabsTrigger value="bess" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">🔋 BESS Activiteit</TabsTrigger>
            <TabsTrigger value="balans" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">⚖️ Energiebalans</TabsTrigger>
            <TabsTrigger value="algo" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">🔄 Algoritme</TabsTrigger>
            <TabsTrigger value="stromen" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">🔀 Energiestromen</TabsTrigger>
            <TabsTrigger value="bronnen" className="text-[11px] font-semibold data-[state=active]:bg-[#0B1F4A] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 border border-[#D9E2EC] data-[state=active]:border-[#0B1F4A]">📚 Bronnen</TabsTrigger>
          </TabsList>

        {/* TAB CONTENT */}
        <div className="bg-white border border-[#D9E2EC] rounded-xl p-5 shadow-sm print:shadow-none print:border-0 print:p-2">

          {/* TAB 0: JAAROVERZICHT */}
          {(tab===0 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-1">📊 Maandelijkse Energiebalans</div>
            <div className="text-[11px] text-[#8899AA] mb-4">Opwek, vraag, grid en BESS activiteit per maand</div>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={r.monthly} margin={{top:5,right:10,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr}/>
                <XAxis dataKey="m" tick={{fontSize:10,fill:C.t3}}/>
                <YAxis tick={{fontSize:10,fill:C.t3}} label={{value:'MWh',angle:-90,position:'insideLeft',style:{fontSize:10,fill:C.t3}}}/>
                <Tooltip content={<CT unit="MWh" descriptions={{pv:"Totale zonne-opwek deze maand",dem:"Totale laadvraag deze maand",gi:"Stroom ingekocht van het net",bu:"Backup generator inzet"}}/>}/>
                <Legend wrapperStyle={{fontSize:10}}/>
                <Bar dataKey="pv" name="PV" fill={C.ch[0]} radius={[3,3,0,0]} barSize={20}/>
                <Bar dataKey="dem" name="Vraag" fill={C.ch[5]} radius={[3,3,0,0]} barSize={20}/>
                <Bar dataKey="gi" name="Grid Import" fill={C.ch[4]} radius={[3,3,0,0]} barSize={20}/>
                <Line dataKey="bu" name="Backup" stroke={C.org} strokeWidth={2} dot={{r:3}}/>
              </ComposedChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#8899AA] mt-2 italic">ℹ️ PV = zonne-opwek, Vraag = energiebehoefte vloot. Grid Import is de hoeveelheid stroom die van het net wordt afgenomen wanneer PV en BESS niet voldoen.</div>
          </div>}

          {/* TAB 1: DAGPROFIEL */}
          {(tab===1 || printMode) && <div className="print-section">
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold text-[#0B1F4A]">🕐 Dagprofiel — {MN[dpMo]}</div>
                <div className="text-[11px] text-[#8899AA]">Gemiddeld uurprofiel (MW)</div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {MN.map((m,i)=>(
                  <button key={i} onClick={()=>setDpMo(i)} className={cn(
                    "px-2 py-1 text-[9px] rounded cursor-pointer font-inherit border",
                    i===dpMo ? "font-bold bg-[#00A3C4] text-white border-[#00A3C4]" : "font-normal bg-transparent text-[#8899AA] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                  )}>{m}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dpData} margin={{top:5,right:10,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr}/>
                <XAxis dataKey="h" tick={{fontSize:9,fill:C.t3}}/>
                <YAxis tick={{fontSize:10,fill:C.t3}} label={{value:'MW',angle:-90,position:'insideLeft',style:{fontSize:10,fill:C.t3}}}/>
                <Tooltip content={<CT unit="MW" descriptions={{pv:"Gemiddelde zonne-opwek dit uur",dem:"Gemiddelde laadvraag dit uur",batDischarge:"Batterij levert stroom (ontlading)",batChargePV:"Batterij laadt op vanuit PV-surplus (negatief)",batChargeBlok:"Batterij laadt op vanuit blokstroom (negatief)",grid:"Import van net (normaal), Negatief = export naar net",gridBlok:"Grid import via blokstroom (daluren)"}}/>}/>
                <Legend wrapperStyle={{fontSize:10}}/>
                <Area type="monotone" dataKey="pv" name="PV" fill={C.ch[0]} fillOpacity={.3} stroke={C.ch[0]} strokeWidth={2}/>
                <Area type="monotone" dataKey="dem" name="Vraag" fill={C.ch[5]} fillOpacity={.15} stroke={C.ch[5]} strokeWidth={2}/>
                <Area type="monotone" dataKey="batDischarge" name="🔋 Ontlading" fill="#10B981" fillOpacity={.25} stroke="#10B981" strokeWidth={2}/>
                <Area type="monotone" dataKey="batChargePV" name="🔋 Laden (PV)" fill="#06B6D4" fillOpacity={.2} stroke="#06B6D4" strokeWidth={1.5}/>
                {blokstroomEnabled && <Area type="monotone" dataKey="batChargeBlok" name="🌙 Laden (blokstroom)" fill="#6366F1" fillOpacity={.25} stroke="#6366F1" strokeWidth={2} strokeDasharray="4 2"/>}
                <Area type="monotone" dataKey="grid" name="Grid (normaal)" fill={C.ch[4]} fillOpacity={.15} stroke={C.ch[4]} strokeWidth={1.5}/>
                {blokstroomEnabled && <Area type="monotone" dataKey="gridBlok" name="🌙 Blokstroom" fill="#6366F1" fillOpacity={.15} stroke="#6366F1" strokeWidth={1} strokeDasharray="2 2"/>}
              </AreaChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#8899AA] mt-2 italic">ℹ️ 🔋 Ontlading = batterij levert stroom (positief). Laden (PV) en Laden (blokstroom) zijn negatief = batterij laadt op.{blokstroomEnabled && ' 🌙 Blokstroom toont laden tijdens daluren.'}</div>
            <div className="mt-3">
              <div className="text-[11px] font-semibold text-[#3D4F6F] mb-1.5">🔋 SOC Verloop</div>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={dpData} margin={{top:5,right:10,left:0,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.bdr}/>
                  <XAxis dataKey="h" tick={{fontSize:9,fill:C.t3}}/>
                  <YAxis domain={[0,100]} tick={{fontSize:9,fill:C.t3}} label={{value:'%',angle:-90,position:'insideLeft',style:{fontSize:10,fill:C.t3}}}/>
                  <Tooltip content={<CT unit="%" descriptions={{soc:"Batterijvulling als percentage van bruikbare capaciteit"}}/>}/>
                  <Area type="monotone" dataKey="soc" name="SOC" fill={C.ch[3]} fillOpacity={.3} stroke={C.ch[3]} strokeWidth={2}/>
                  <ReferenceLine y={100} stroke={C.rd} strokeDasharray="3 3" label={{value:'Max',position:'right',style:{fontSize:8,fill:C.rd}}}/>
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Mini laadprofiel + seizoensfactoren charts */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              {/* Laadprofiel */}
              <div className="border border-[#D9E2EC] rounded-lg p-3 bg-[#F5F8FB]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold text-[#0B1F4A]">⚡ Laadprofiel</span>
                  <span className={cn(
                    "text-[9px] font-bold px-2 py-0.5 rounded-full",
                    profile.id === 'wognum'
                      ? "bg-[#0B1F4A] text-white"
                      : "bg-[#00A3C4] text-white"
                  )}>
                    {profile.id === 'wognum' ? 'Twee-pieks (nacht 72% / dag 28%)' : 'Nachtlading (depot)'}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={80}>
                  <BarChart data={activeCHP.map((v, i) => {
                    const total = activeCHP.reduce((a, b) => a + b, 0);
                    return { h: `${String(i).padStart(2,'0')}:00`, pct: Math.round((v / total) * 1000) / 10 };
                  })} margin={{top:0,right:0,left:0,bottom:0}}>
                    <XAxis dataKey="h" tick={{fontSize:7,fill:C.t3}} interval={2}/>
                    <YAxis hide/>
                    <Tooltip formatter={(val: number) => [`${val}%`, 'Aandeel']} labelStyle={{fontSize:10}} contentStyle={{fontSize:10}}/>
                    <Bar dataKey="pct" radius={[2,2,0,0]} barSize={12}>
                      {activeCHP.map((_, i) => (
                        <Cell key={i} fill={i >= 18 || i < 6 ? '#0B1F4A' : '#00A3C4'}/>
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex gap-3 justify-center mt-1">
                  <div className="flex items-center gap-1 text-[9px] text-[#3D4F6F]"><div className="w-2 h-2 rounded-sm bg-[#0B1F4A]"/>Nacht (18:00–06:00)</div>
                  <div className="flex items-center gap-1 text-[9px] text-[#3D4F6F]"><div className="w-2 h-2 rounded-sm bg-[#00A3C4]"/>Dag (06:00–18:00)</div>
                </div>
              </div>

              {/* Seizoensfactoren */}
              <div className="border border-[#D9E2EC] rounded-lg p-3 bg-[#F5F8FB]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold text-[#0B1F4A]">📅 Seizoensfactoren</span>
                  <span className={cn(
                    "text-[9px] font-bold px-2 py-0.5 rounded-full",
                    CSF.some(v => v > 1.15 || v < 0.85)
                      ? "bg-[#0B1F4A] text-white"
                      : "bg-[#00A3C4] text-white"
                  )}>
                    {CSF.some(v => v > 1.15 || v < 0.85)
                      ? `Seizoenspiek Q${Math.floor(CSF.indexOf(Math.max(...CSF)) / 3) + 1}`
                      : 'Vlak profiel'}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={80}>
                  <BarChart data={(() => {
                    const avg = CSF.reduce((a, b) => a + b, 0) / CSF.length;
                    return CSF.map((v, i) => ({ m: MN[i], val: Math.round(v * 100) / 100, above: v >= avg }));
                  })()} margin={{top:0,right:0,left:0,bottom:0}}>
                    <XAxis dataKey="m" tick={{fontSize:7,fill:C.t3}}/>
                    <YAxis hide/>
                    <Tooltip formatter={(val: number) => [`${val}×`, 'Factor']} labelStyle={{fontSize:10}} contentStyle={{fontSize:10}}/>
                    <Bar dataKey="val" radius={[2,2,0,0]} barSize={16}>
                      {CSF.map((v, i) => {
                        const avg = CSF.reduce((a, b) => a + b, 0) / CSF.length;
                        return <Cell key={i} fill={v >= avg ? '#0B1F4A' : '#00A3C4'}/>;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex gap-3 justify-center mt-1">
                  <div className="flex items-center gap-1 text-[9px] text-[#3D4F6F]"><div className="w-2 h-2 rounded-sm bg-[#0B1F4A]"/>Boven gem.</div>
                  <div className="flex items-center gap-1 text-[9px] text-[#3D4F6F]"><div className="w-2 h-2 rounded-sm bg-[#00A3C4]"/>Onder gem.</div>
                </div>
              </div>
            </div>
          </div>}

          {/* TAB 2: ZELFVOORZIENING TREND */}
          {(tab===2 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-1">📈 Maandelijkse Zelfvoorzieningstrend</div>
            <div className="text-[11px] text-[#8899AA] mb-4">Percentage lokaal geleverd (PV direct + BESS) per maand</div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={r.monthly} margin={{top:5,right:10,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr}/>
                <XAxis dataKey="m" tick={{fontSize:10,fill:C.t3}}/>
                <YAxis domain={[0,100]} tick={{fontSize:10,fill:C.t3}} label={{value:'%',angle:-90,position:'insideLeft',style:{fontSize:10,fill:C.t3}}}/>
                <Tooltip content={<CT unit="%" descriptions={{mSS:"Percentage van de vraag dat lokaal wordt geleverd (PV + BESS, zonder grid)"}}/>}/>
                <ReferenceLine y={85} stroke={C.grn} strokeDasharray="3 3" label={{value:'85% doel',position:'right',style:{fontSize:9,fill:C.grn}}}/>
                <ReferenceLine y={70} stroke={C.org} strokeDasharray="3 3" label={{value:'70% min',position:'right',style:{fontSize:9,fill:C.org}}}/>
                <Bar dataKey="mSS" name="Zelfvoorziening" radius={[4,4,0,0]} barSize={30}>
                  {r.monthly.map((entry: any,i: number)=><Cell key={i} fill={entry.mSS>=85?C.grn:entry.mSS>=70?C.org:C.rd}/>)}
                </Bar>
                <Line dataKey="mSS" name="Trend" stroke={C.pri} strokeWidth={2} dot={false} type="monotone"/>
              </ComposedChart>
            </ResponsiveContainer>
            <div className="flex gap-3 justify-center mt-3">
              {[{c:C.grn,l:'≥85% Uitstekend'},{c:C.org,l:'70-85% Redelijk'},{c:C.rd,l:'<70% Aandacht'}].map((it,i)=>
                <div key={i} className="flex items-center gap-1 text-[10px] text-[#3D4F6F]">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{background:it.c}}/>{it.l}
                </div>
              )}
            </div>
          </div>}

          {/* TAB 3: BESS ACTIVITEIT */}
          {(tab===3 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-1">🔋 BESS Maandelijkse Activiteit</div>
            <div className="text-[11px] text-[#8899AA] mb-4">Laden, ontladen en netto energiestromen per maand</div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={r.monthly} margin={{top:5,right:10,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.bdr}/>
                <XAxis dataKey="m" tick={{fontSize:10,fill:C.t3}}/>
                <YAxis tick={{fontSize:10,fill:C.t3}} label={{value:'MWh',angle:-90,position:'insideLeft',style:{fontSize:10,fill:C.t3}}}/>
                <Tooltip content={<CT unit="MWh" descriptions={{bIn:"Totale energie geladen in BESS",bOut:"Totale energie ontladen uit BESS"}}/>}/>
                <Legend wrapperStyle={{fontSize:10}}/>
                <Bar dataKey="bIn" name="Laden" fill={C.ch[1]} radius={[3,3,0,0]} barSize={18}/>
                <Bar dataKey="bOut" name="Ontladen" fill={C.ch[3]} radius={[3,3,0,0]} barSize={18}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>}

          {/* TAB 4: ENERGIEBALANS */}
          {(tab===4 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-3">⚖️ Jaarlijkse Energiebalans</div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-[10px] font-semibold text-[#8899AA] uppercase mb-1.5">Aanbod</div>
                {[{l:'PV Productie',v:r.pv,c:C.ch[0]},{l:'Grid Import',v:r.gi,c:C.ch[4]},{l:'Backup',v:r.bu,c:C.org}].map((it,i)=>(
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <div className="w-2 h-2 rounded-sm shrink-0" style={{background:it.c}}/>
                    <div className="flex-1 text-[11px] text-[#3D4F6F]">{it.l}</div>
                    <div className="text-[11px] font-bold text-[#0B1F4A] tabular-nums">{it.v.toFixed(0)} MWh</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8899AA] uppercase mb-1.5">Vraag & Verliezen</div>
                {[
                  {l:'Laadvraag',v:r.dem,c:C.ch[5]},
                  {l:'BESS Verliezen',v:r.bLoss,c:C.org},
                  {l:'Grid Export',v:r.ge,c:C.ch[3]},
                  {l:'Curtailment',v:r.curt,c:'#94A3B8'},
                  {l:'Ongedekte Vraag',v:r.unmet,c:C.rd},
                ].map((it,i)=>(
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <div className="w-2 h-2 rounded-sm shrink-0" style={{background:it.c}}/>
                    <div className="flex-1 text-[11px] text-[#3D4F6F]">{it.l}</div>
                    <div className="text-[11px] font-bold text-[#0B1F4A] tabular-nums">{it.v.toFixed(0)} MWh</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 text-center">
              <div className="text-xs font-semibold text-[#3D4F6F] mb-2">Leveringsmix</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={supplyMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {supplyMix.map((entry,i)=><Cell key={i} fill={entry.color}/>)}
                  </Pie>
                  <Tooltip formatter={(v: any)=>`${Number(v).toFixed(0)} MWh`}/>
                  <Legend wrapperStyle={{fontSize:10}}/>
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
              <div className="bg-[#F5F8FB] rounded-lg px-3.5 py-2.5 border border-[#D9E2EC]">
                <div className="text-[10px] text-[#8899AA]">PV Benutting</div>
                <div className="text-base font-bold" style={{color:C.ch[0]}}>{r.pvUtil.toFixed(1)}%</div>
              </div>
              <div className="bg-[#F5F8FB] rounded-lg px-3.5 py-2.5 border border-[#D9E2EC]">
                <div className="text-[10px] text-[#8899AA]">PV / Vraag Ratio</div>
                <div className="text-base font-bold text-[#0B1F4A]">{r.dem>0?(r.pv/r.dem).toFixed(2):'—'}x</div>
              </div>
              <div className="bg-[#F5F8FB] rounded-lg px-3.5 py-2.5 border border-[#D9E2EC]">
                <div className="text-[10px] text-[#8899AA]">Grid Afhankelijkheid</div>
                <div className="text-base font-bold" style={{color:C.ch[4]}}>{r.dem>0?(r.gi/r.dem*100).toFixed(1):'—'}%</div>
              </div>
            </div>

            {/* ENERGY BALANCE VALIDATION */}
            <div className="mt-6 border border-[#D9E2EC] rounded-xl p-4 bg-[#F5F8FB]">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-bold text-[#0B1F4A]">✅ Energiebalans Validatie</span>
                {validation.deviation < 0.01 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]">
                    Balans klopt (afwijking: {validation.deviation.toFixed(4)}%)
                  </span>
                ) : validation.deviation < 1 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">
                    Kleine afwijking: {validation.deviation.toFixed(4)}%
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]">
                    Afwijking: {validation.deviation.toFixed(2)}%
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-semibold text-[#8899AA] uppercase mb-1.5">Aanbod</div>
                  {[{l:'PV Productie',v:r.pv},{l:'Grid Import',v:r.gi},{l:'Backup',v:r.bu}].map((it,i)=>(
                    <div key={i} className="flex justify-between text-[11px] text-[#3D4F6F] mb-0.5">
                       <span>{it.l}</span><span className="font-semibold tabular-nums">{it.v.toFixed(1)} MWh</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[11px] font-bold text-[#0B1F4A] border-t border-[#D9E2EC] mt-1 pt-1">
                    <span>Totaal Aanbod</span><span className="tabular-nums">{validation.supply.toFixed(1)} MWh</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-[#8899AA] uppercase mb-1.5">Vraag & Verliezen</div>
                  {[{l:'Laadvraag',v:r.dem},{l:'BESS Verliezen',v:r.bLoss},{l:'Grid Export',v:r.ge},{l:'Curtailment',v:r.curt}].map((it,i)=>(
                    <div key={i} className="flex justify-between text-[11px] text-[#3D4F6F] mb-0.5">
                      <span>{it.l}</span><span className="font-semibold tabular-nums">{it.v.toFixed(1)} MWh</span>
                    </div>
                  ))}
                  {/* Was "(informatief)". Dat klopte niet meer: dit getal gaat nu
                      als `unmet` mee naar de businesscase en wordt van de
                      omzetbasis afgetrokken. */}
                  {r.unmet > 0.0001 && (
                    <div className="flex justify-between text-[11px] text-[#991B1B] mb-0.5">
                      <span>Ongedekte Vraag (niet geleverd, niet gefactureerd)</span><span className="font-semibold tabular-nums">{r.unmet.toFixed(1)} MWh</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[11px] font-bold text-[#0B1F4A] border-t border-[#D9E2EC] mt-1 pt-1">
                    <span>Totaal Vraag</span><span className="tabular-nums">{validation.demand.toFixed(1)} MWh</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-[#8899AA] text-center">
                Formule: |Aanbod − Vraag| / Aanbod × 100 = |{validation.balance.toFixed(6)}| / {validation.supply.toFixed(4)} × 100 = {validation.deviation.toFixed(4)}%
              </div>
            </div>
          </div>}

          {/* TAB 5: ALGORITME */}
          {(tab===5 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-1">🔄 Dispatch Algoritme — Merit-Order Logic</div>
            <div className="text-[11px] text-[#8899AA] mb-4">Hoe het model elk uur van het jaar de energieverdeling berekent</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border border-[#A7F3D0] rounded-xl p-4 bg-[#F0FDF4]">
                <div className="text-xs font-bold text-[#065F46] mb-3">☀️ Overschot-pad (PV {'>'} Vraag)</div>
                {[
                  {step:'1',label:'PV Direct',desc:'Volledige vraag gedekt',formula:'direct = demH',color:C.sec,arrow:true,tip:'Alle beschikbare zonnestroom gaat eerst naar de laadvraag'},
                  {step:'2',label:'BESS Laden',desc:'Surplus → batterij',formula:'min(surplus, bP, (usable−SOC)/√eff)',color:C.ch[1],arrow:true,tip:'Het surplus wordt opgeslagen in de batterij'},
                  {step:'3',label:'Grid Export',desc:'Rest → net',formula:`min(surplus, ${gMax} MW)`,color:C.ch[4],arrow:true,tip:`Overtollige stroom wordt verkocht aan het net, max ${gMax} MW`},
                  {step:'4',label:'Curtailment',desc:'Onbenut overschot',formula:'resterend surplus',color:'#94A3B8',arrow:false,tip:'Zonnestroom die niet benut kan worden'},
                ].map((s,i)=>(
                  <div key={i} title={s.tip}>
                    <div className="flex items-start gap-2 mb-1">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{background:s.color}}>{s.step}</div>
                      <div className="flex-1">
                        <div className="text-[11px] font-semibold text-[#0B1F4A]">{s.label}</div>
                        <div className="text-[10px] text-[#3D4F6F]">{s.desc}</div>
                        <code className="text-[9px] text-[#8899AA] bg-white px-1.5 py-0.5 rounded border border-[#D9E2EC] inline-block mt-0.5">{s.formula}</code>
                        <div className="text-[9px] text-[#6B7F99] italic mt-0.5">💡 {s.tip}</div>
                      </div>
                    </div>
                    {s.arrow && <div className="ml-2.5 text-[#A7F3D0] text-xs leading-none mb-1">↓</div>}
                  </div>
                ))}
              </div>

              <div className="border border-[#FECACA] rounded-xl p-4 bg-[#FEF2F2]">
                <div className="text-xs font-bold text-[#991B1B] mb-3">🌙 Tekort-pad (PV {'<'} Vraag)</div>
                {[
                  {step:'1',label:'PV Direct',desc:'Alle PV benut',formula:'direct = pvH',color:C.sec,arrow:true,tip:'Alle beschikbare zonnestroom gaat eerst naar de laadvraag'},
                  {step:'2',label:'BESS Ontladen',desc:'Batterij levert tekort',formula:'min(deficit, bP, SOC×√eff)',color:C.ch[1],arrow:true,tip:'De batterij levert stroom tot het tekort gedekt is'},
                  {step:'3',label:'Grid Import',desc:'Net vult aan',formula:`min(deficit, ${gMax} MW)`,color:C.ch[4],arrow:true,tip:'Stroom wordt ingekocht van het net'},
                  {step:'4',label:'Backup Generator',desc:'Noodvoorziening',formula:'min(deficit, buP)',color:C.org,arrow:true,tip:'De noodgenerator springt bij'},
                  {step:'5',label:'Ongedekte Vraag',desc:'Resterend tekort',formula:'deficit > 0.0001',color:C.rd,arrow:false,tip:'Vraag die niet gedekt kan worden'},
                ].map((s,i)=>(
                  <div key={i} title={s.tip}>
                    <div className="flex items-start gap-2 mb-1">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{background:s.color}}>{s.step}</div>
                      <div className="flex-1">
                        <div className="text-[11px] font-semibold text-[#0B1F4A]">{s.label}</div>
                        <div className="text-[10px] text-[#3D4F6F]">{s.desc}</div>
                        <code className="text-[9px] text-[#8899AA] bg-white px-1.5 py-0.5 rounded border border-[#D9E2EC] inline-block mt-0.5">{s.formula}</code>
                        <div className="text-[9px] text-[#6B7F99] italic mt-0.5">💡 {s.tip}</div>
                      </div>
                    </div>
                    {s.arrow && <div className="ml-2.5 text-[#FECACA] text-xs leading-none mb-1">↓</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4 mb-6">
              <div className="text-xs font-bold text-[#0B1F4A] mb-2">🔋 BESS Efficiency — √η Methode</div>
              <div className="text-[11px] text-[#3D4F6F] leading-relaxed">
                Round-trip efficiency ({eff}%) wordt gesplitst als <code className="bg-white px-1 rounded border border-[#D9E2EC] text-[10px]">√{eff/100} = {Math.sqrt(eff/100).toFixed(4)}</code> per richting.
                <br/>Bij <strong>laden</strong>: SOC += energie × √η
                <br/>Bij <strong>ontladen</strong>: geleverd = min(tekort, SOC × √η) → SOC -= geleverd / √η
                <br/>SOC wordt geclampt op [0, {usable.toFixed(1)} MWh] (= {effectiveBE.toFixed(1)} MWh × {dod}% DoD)
              </div>
            </div>

            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 mb-6">
              <div className="text-xs font-bold text-[#0B1F4A] mb-2">📉 Batterijdegradatie</div>
              <div className="text-[11px] text-[#3D4F6F] leading-relaxed">
                Effectieve capaciteit = nominaal × (1 − degradatie%)^(jaar−1)
                <br/><code className="bg-white px-1 rounded border border-[#D9E2EC] text-[10px]">{bE} × (1 − {deg/100})^({projYear}−1) = {effectiveBE.toFixed(2)} MWh</code>
                <br/>Bij {deg}%/jaar:
                <br/>• Jaar 10: {(bE * Math.pow(1-deg/100, 9)).toFixed(1)} MWh ({(Math.pow(1-deg/100, 9)*100).toFixed(1)}%)
                <br/>• Jaar 20: {(bE * Math.pow(1-deg/100, 19)).toFixed(1)} MWh ({(Math.pow(1-deg/100, 19)*100).toFixed(1)}%)
              </div>
            </div>

            {/* LIVE HOUR CALCULATION */}
            <div className="border border-[#D9E2EC] rounded-xl p-4 bg-white">
              <div className="text-xs font-bold text-[#0B1F4A] mb-3">🔬 Live Uur-Berekening</div>
              <div className="text-[10px] text-[#8899AA] mb-3">Selecteer een maand en uur om de exacte dispatch-stappen te zien</div>

              <div className="flex gap-4 items-end mb-4 flex-wrap">
                <div>
                  <div className="text-[10px] font-semibold text-[#3D4F6F] mb-1">Maand</div>
                  <div className="flex gap-1 flex-wrap">
                    {MN.map((m,i)=>(
                      <button key={i} onClick={()=>setAlgoMonth(i)} className={cn(
                        "px-2 py-1 text-[9px] rounded cursor-pointer font-inherit border",
                        i===algoMonth ? "font-bold bg-[#00A3C4] text-white border-[#00A3C4]" : "font-normal bg-transparent text-[#8899AA] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                      )}>{m}</button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] font-semibold text-[#3D4F6F] mb-1">Uur: <strong className="text-[#0B1F4A]">{algoHour}:00</strong></div>
                  <input type="range" min={0} max={23} step={1} value={algoHour} onChange={e=>setAlgoHour(+e.target.value)}
                    className="w-full h-1.5 appearance-none rounded-sm cursor-pointer"
                    style={{background:`linear-gradient(to right, ${C.sec} 0%, ${C.sec} ${(algoHour/23)*100}%, ${C.bdr} ${(algoHour/23)*100}%, ${C.bdr} 100%)`}}
                  />
                </div>
              </div>

              <div className={cn(
                "rounded-xl p-4 border",
                algoDispatch.isOverschot ? "bg-[#F0FDF4] border-[#A7F3D0]" : "bg-[#FEF2F2] border-[#FECACA]"
              )}>
                <div className="text-[11px] font-bold mb-2" style={{color: algoDispatch.isOverschot ? '#065F46' : '#991B1B'}}>
                  {algoDispatch.isOverschot ? '☀️ Overschot' : '🌙 Tekort'} — {MN[algoMonth]} {algoHour}:00
                </div>

                <div className="space-y-1.5">
                  {algoDispatch.steps.map((step,i)=>{
                    const mw = step.value.toFixed(3);
                    const pct = usable > 0 ? (algoDispatch.socBefore/usable*100).toFixed(1) : '0.0';
                    const humanDesc = step.label === 'PV Direct' ? `${mw} MW zonnestroom wordt direct gebruikt`
                      : step.label === 'BESS Laden' ? `${mw} MW wordt opgeslagen in de batterij (vulniveau: ${pct}%)`
                      : step.label === 'BESS Ontladen' ? `De batterij levert ${mw} MW vanuit een vulniveau van ${pct}%`
                      : step.label === 'Grid Export' ? `${mw} MW wordt geëxporteerd naar het net`
                      : step.label === 'Grid Import' ? `Het net levert ${mw} MW aan stroom`
                      : step.label === 'Backup Generator' ? `De noodgenerator levert ${mw} MW`
                      : step.label === 'Curtailment' ? `${mw} MW zonnestroom kan niet benut worden`
                      : step.label === 'Ongedekte Vraag' ? `${mw} MW kan niet worden gedekt door alle bronnen`
                      : '';
                    return (
                    <div key={i} className="flex items-start gap-2 bg-white/80 rounded-lg px-3 py-2 border border-white/50">
                      <div className="w-1.5 h-full rounded-full shrink-0 mt-1" style={{background:step.color, minHeight:'24px'}}/>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline gap-2">
                          <span className="text-[11px] font-semibold text-[#0B1F4A]">{step.label}</span>
                          <span className="text-[11px] font-bold tabular-nums shrink-0" style={{color:step.color}}>
                            {mw} MW
                          </span>
                        </div>
                        <code className="text-[9px] text-[#8899AA] block truncate">{step.formula}</code>
                        {step.value > 0.0001 && <div className="text-[9px] text-[#6B7F99] italic mt-0.5">→ {humanDesc}</div>}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div className="mt-3 flex gap-3 text-[10px]">
                  <div className="bg-white rounded-lg px-3 py-2 border border-[#D9E2EC] flex-1">
                    <div className="text-[#8899AA]">SOC voor</div>
                    <div className="font-bold text-[#0B1F4A] tabular-nums">{algoDispatch.socBefore.toFixed(2)} MWh ({(algoDispatch.socBefore/usable*100).toFixed(1)}%)</div>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2 border border-[#D9E2EC] flex-1">
                    <div className="text-[#8899AA]">SOC na</div>
                    <div className="font-bold text-[#0B1F4A] tabular-nums">{algoDispatch.socAfter.toFixed(2)} MWh ({(algoDispatch.socAfter/usable*100).toFixed(1)}%)</div>
                  </div>
                </div>
                <div className="mt-2 text-[9px] text-[#8899AA] italic">
                  SOC = State of Charge — het energieniveau van de batterij als percentage van de bruikbare capaciteit ({usable.toFixed(1)} MWh).
                </div>
              </div>
            </div>
          </div>}

          {/* TAB 6: ENERGIESTROMEN (SANKEY) */}
          {(tab===6 || printMode) && <div className="print-section"><SankeyFlow r={r} pv={pv} bP={bP} bE={bE} effectiveBE={effectiveBE} dod={dod} gMax={gMax} buP={buP} dem={resolvedDem} blokstroomImport={r.blokstroomImport} blokstroomEnabled={blokstroomEnabled} /></div>}

          {/* TAB 8: BUSINESS CASE */}
          {tab===8 && <FinancialDashboard params={financialParams} onParamChange={handleFinancialParamChange} model={financialModel} sys={financialSys} sim={financialSim} degradation={deg} />}

          {/* TAB 7: BRONNEN */}
          {(tab===7 || printMode) && <div className="print-section">
            <div className="text-sm font-bold text-[#0B1F4A] mb-3">📚 Bronnen & Methode</div>

            <div className="mb-5">
              <div className="text-xs font-semibold text-[#3D4F6F] mb-2">Vlootsamenstelling {displayClient} — {numTrucks} voertuigen</div>
              <table className="w-full border-collapse text-[10px]">
                <thead>
                  <tr className="bg-[#F5F8FB]">
                    <th className="px-2.5 py-1.5 text-left border-b border-[#D9E2EC] text-[#8899AA]">Type</th>
                    <th className="px-2.5 py-1.5 text-right border-b border-[#D9E2EC] text-[#8899AA]">Aantal</th>
                    <th className="px-2.5 py-1.5 text-right border-b border-[#D9E2EC] text-[#8899AA]">MWh/j</th>
                  </tr>
                </thead>
                <tbody>
                  {FLEET.map((f,i)=>(
                    <tr key={i} className="border-b border-[#D9E2EC]">
                      <td className="px-2.5 py-1.5 text-[#3D4F6F]">{f.type}</td>
                      <td className="px-2.5 py-1.5 text-right font-semibold text-[#0B1F4A]">{f.n}</td>
                      <td className="px-2.5 py-1.5 text-right font-semibold text-[#0B1F4A]">{f.mwh}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#F5F8FB] font-bold">
                    <td className="px-2.5 py-1.5 text-[#0B1F4A]">Totaal</td>
                    <td className="px-2.5 py-1.5 text-right text-[#0B1F4A]">{FLEET.reduce((s,f)=>s+f.n,0)}</td>
                    <td className="px-2.5 py-1.5 text-right text-[#0B1F4A]">{FLEET.reduce((s,f)=>s+f.mwh,0).toLocaleString('nl-NL')}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {DOCS.map((cat,ci)=>(
              <div key={ci} className="mb-4">
                <div className="text-xs font-semibold text-[#0B1F4A] mb-2">{cat.cat}</div>
                <table className="w-full border-collapse text-[10px]">
                  <thead>
                    <tr className="bg-[#F5F8FB]">
                      {['Parameter','Default','Range','Bron','Opmerking'].map((h,i)=>(
                        <th key={i} className="px-2 py-1.5 text-left border-b border-[#D9E2EC] text-[#8899AA] font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cat.params.map((p,i)=>(
                      <tr key={i} className="border-b border-[#D9E2EC]">
                        <td className="px-2 py-1.5 font-semibold text-[#0B1F4A]">{p.l}</td>
                        <td className="px-2 py-1.5 text-[#3D4F6F]">{p.d}</td>
                        <td className="px-2 py-1.5 text-[#3D4F6F]">{p.r}</td>
                        <td className="px-2 py-1.5 text-[#8899AA] text-[9px]">{p.s}</td>
                        <td className="px-2 py-1.5 text-[#8899AA] text-[9px]">{p.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            <div className="text-[10px] text-[#8899AA] leading-relaxed mt-3 p-3 bg-[#F5F8FB] rounded-lg">
              <strong>Modelbeperkingen:</strong> Dit model is indicatief. Niet meegenomen: temperatuureffecten,
              vermogensomzetterverliezen, exacte laadcurves per voertuig, netcongestie, TOU-tarieven, seizoensgebonden onderhoud.
              Batterijdegradatie is gemodelleerd als lineair capaciteitsverlies per jaar. PV-degradatie is niet meegenomen.
              Het laadprofiel is synthetisch (TNO/CE Delft); met echte operationele data worden de resultaten nauwkeuriger.
              <br/><br/>
              <strong>Bronnen:</strong> {displayCompany} (systeemontwerp), {displayClient} (vlootdata),
              TNO/CE Delft (laadpatronen), NREL/BloombergNEF (BESS parameters).
            </div>
          </div>}
        </div>
        </Tabs>

        {/* COLLAPSIBLE INSIGHTS PANEL */}
        <div className={cn("mt-4 bg-white border border-[#D9E2EC] rounded-xl shadow-sm print:hidden", printMode && "hidden")}>
          <button
            onClick={() => setInsightsOpen(!insightsOpen)}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer bg-transparent border-none font-inherit"
          >
            <span className="text-xs font-bold text-[#0B1F4A]">💡 Automatische Inzichten</span>
            <span className={cn("text-[#8899AA] transition-transform text-sm", insightsOpen && "rotate-180")}>▼</span>
          </button>
          {insightsOpen && (
            <div className="px-4 pb-3.5 flex flex-col gap-1.5">
              {insights.map((ins: any, i: number) => (
                <div key={i} className={cn(
                  "flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] leading-relaxed border",
                  ins.type==='success' && "bg-[#F0FDF4] border-[#BBF7D0]",
                  ins.type==='warn' && "bg-[#FFFBEB] border-[#FDE68A]",
                  ins.type==='alert' && "bg-[#FEF2F2] border-[#FECACA]",
                  ins.type==='info' && "bg-[#F0F9FF] border-[#BFDBFE]",
                )}>
                  <span className="text-sm shrink-0">{ins.icon}</span>
                  <span className="text-[#3D4F6F]">{ins.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PRINT-ONLY: Financial Summary */}
        {printMode && <div id="print-financial-summary" className="mt-4 p-4 bg-[#F5F8FB] rounded-xl border border-[#D9E2EC]" data-pdf-section="financial">
          <div className="text-sm font-bold text-[#0B1F4A] mb-1">📄 Financiële Samenvatting — Business Case</div>
          <div className="text-[10px] text-[#8899AA] mb-3">
            Geprint op {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">NPV</div>
              <div className={`text-sm font-bold ${financialModel.kpis.npv >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                €{(financialModel.kpis.npv / 1e6).toFixed(2)}M
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">IRR</div>
              <div className="text-sm font-bold text-[#0B1F4A]">
                {isNaN(financialModel.kpis.irr) ? '—' : `${financialModel.kpis.irr.toFixed(1)}%`}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">Terugverdientijd</div>
              <div className="text-sm font-bold text-[#0B1F4A]">
                {financialModel.kpis.paybackYear !== null ? `${financialModel.kpis.paybackYear.toFixed(1)} jaar` : '> projectduur'}
              </div>
            </div>
            {/* LCOE en marge via `formatEurPerKwh`: bij nul geleverde energie is
                de LCOE oneindig en de marge −oneindig, en toont deze strook "n.v.t.".
                De oude `toFixed(3)` maakte daar op de Wognum-invoer met pv 0, gMax 0,
                bP 0, buP 0 en 19.000 MWh vraag (100 % ongedekt) €0,000 LCOE en
                €0,300 marge van — winst uit niet-geleverde stroom. */}
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">LCOE</div>
              <div className="text-sm font-bold text-[#0B1F4A]">{formatEurPerKwh(financialModel.kpis.lcoe)}</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">Marge</div>
              <div className={`text-sm font-bold ${financialModel.kpis.marginPerKwh >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {formatEurPerKwh(financialModel.kpis.marginPerKwh)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-[#D9E2EC]">
              <div className="text-[9px] text-[#8899AA]">ROI</div>
              <div className={`text-sm font-bold ${financialModel.kpis.roi >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {financialModel.kpis.roi.toFixed(1)}%
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[#8899AA]">
            <strong>Totale CAPEX:</strong> €{(financialModel.kpis.totalCapex / 1e6).toFixed(2)}M
            {' | '}PV: €{(financialModel.capex.pv / 1e6).toFixed(1)}M
            {' | '}BESS: €{(financialModel.capex.bess / 1e6).toFixed(1)}M
            {' | '}Laadinfra: €{(financialModel.capex.chargers / 1e6).toFixed(1)}M
            {' | '}Overig: €{((financialModel.capex.backup + financialModel.capex.gridConnection + financialModel.capex.engineering) / 1e6).toFixed(1)}M
          </div>
        </div>}


        <div className="mt-3 py-2 border-t border-border text-center">
          <div className="text-[9px] text-muted-foreground">Indicatief model. Niet geschikt voor besluitvorming zonder professionele validatie.</div>
        </div>
      </div>
    </div>
  );
}

export default EnergyParkDashboard;
